import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { notifyEvent } from '@/lib/notify'
import { playNotifySound } from '@/lib/sound'
import { setFaviconBadge } from '@/lib/favicon'
import { isTicketUrgent } from '@/lib/utils'
import {
  type TimeFilter,
  ALL_TIME,
  matchesTimeFilter,
} from '@/lib/timeFilter'
import type {
  Folder,
  List,
  Project,
  ProjectMember,
  ProposedSplit,
  Ticket,
  UserPreferences,
  UserProfile,
} from '@/lib/types'
import { DEFAULT_THEME, isThemeKey } from '@/lib/themes'
import i18n, { resolveLocale } from '@/i18n'
import { ProjectSidebar } from '@/components/ProjectSidebar'
import { KanbanBoard } from '@/components/KanbanBoard'
import { BoardListView } from '@/components/BoardListView'
import { MergeCardsModal } from '@/components/MergeCardsModal'
import { TicketDetailModal } from '@/components/TicketDetailModal'
import { LoginScreen } from '@/components/LoginScreen'
import { Onboarding } from '@/components/Onboarding'
import { MfaChallenge } from '@/components/MfaChallenge'
import { MediaLightbox } from '@/components/MediaLightbox'
import { AudioPanel } from '@/components/AudioPanel'
import { HoverPreview } from '@/components/HoverPreview'
import { TopBar } from '@/components/TopBar'
import { ProfileModal } from '@/components/ProfileModal'
import { ArchivedCardsModal } from '@/components/ArchivedCardsModal'
import { ProjectMembersModal } from '@/components/ProjectMembersModal'
import { MoveCardDialog } from '@/components/MoveCardDialog'
import { ReplyDialog } from '@/components/ReplyDialog'
import { ProjectSettingsModal } from '@/components/ProjectSettingsModal'
import { InviteToCardsModal } from '@/components/InviteToCardsModal'
import { useNotify, useConfirm, useUndoToast } from '@/components/ConfirmDialog'
import {
  AUDIO_SPEEDS,
  DEFAULT_AUDIO_SPEED,
  type AudioSpeed,
} from '@/components/InlineAudio'
import { LayoutGrid, Loader2 } from 'lucide-react'

// Captured at import time, before supabase-js strips the auth hash from the URL.
const ARRIVED_VIA_INVITE = /type=(invite|recovery)/.test(window.location.hash)

/** Auth gate: login → (set password for invites) → (2FA challenge) → board. */
export default function App() {
  const [session, setSession] = useState<Session | null>(null)
  const [authLoading, setAuthLoading] = useState(true)
  // 'unknown' until the Authenticator Assurance Level has been checked.
  const [mfaState, setMfaState] = useState<'unknown' | 'ok' | 'required'>(
    'unknown',
  )
  // Set when the user landed via an invite / password-reset link.
  const [needsPassword, setNeedsPassword] = useState(ARRIVED_VIA_INVITE)

  const refreshMfa = useCallback(async () => {
    const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
    if (data && data.nextLevel === 'aal2' && data.currentLevel !== 'aal2') {
      setMfaState('required')
    } else {
      setMfaState('ok')
    }
  }, [])

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      setAuthLoading(false)
      if (data.session) refreshMfa()
      else setMfaState('ok')
    })
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s)
      if (event === 'PASSWORD_RECOVERY') setNeedsPassword(true)
      if (s) {
        setMfaState('unknown')
        refreshMfa()
      } else {
        setMfaState('ok')
        setNeedsPassword(false)
      }
    })
    return () => sub.subscription.unsubscribe()
  }, [refreshMfa])

  if (authLoading || (session && mfaState === 'unknown')) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-[var(--color-bg)] text-sm text-[var(--color-text-dim)]">
        Chargement…
      </div>
    )
  }
  if (!session) {
    if (ARRIVED_VIA_INVITE) {
      return (
        <div className="flex h-screen w-screen items-center justify-center bg-[var(--color-bg)] text-sm text-[var(--color-text-dim)]">
          Chargement…
        </div>
      )
    }
    return <LoginScreen />
  }
  if (needsPassword) {
    return (
      <SetPasswordScreen
        onDone={() => {
          setNeedsPassword(false)
          history.replaceState(
            null,
            '',
            window.location.pathname + window.location.search,
          )
        }}
        onSignOut={() => supabase.auth.signOut()}
      />
    )
  }
  if (mfaState === 'required') {
    return (
      <MfaChallenge
        onVerified={refreshMfa}
        onSignOut={() => supabase.auth.signOut()}
      />
    )
  }

  return (
    <Board
      key={session.user.id}
      user={session.user}
      onSignOut={() => supabase.auth.signOut()}
    />
  )
}

/** Password setup for users arriving via an invite or password-reset link. */
function SetPasswordScreen({
  onDone,
  onSignOut,
}: {
  onDone: () => void
  onSignOut: () => void
}) {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (password.length < 6) {
      setError('6 caractères minimum.')
      return
    }
    if (password !== confirm) {
      setError('Les mots de passe ne correspondent pas.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const { error: err } = await supabase.auth.updateUser({ password })
      if (err) throw err
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-[var(--color-bg)] px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-2.5 text-center">
          <img
            src="/favicon.svg"
            alt="msgban"
            className="h-14 w-14 rounded-2xl shadow-[var(--shadow-soft)]"
          />
          <div>
            <div className="text-[18px] font-semibold text-[var(--color-text-strong)]">
              Bienvenue sur msgban
            </div>
            <div className="text-[12px] text-[var(--color-text-dim)]">
              Choisis un mot de passe pour ton compte.
            </div>
          </div>
        </div>
        <form
          onSubmit={submit}
          className="rounded-3xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] p-6 shadow-[var(--shadow-modal)]"
        >
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium text-[var(--color-text-dim)]">
              Mot de passe
            </span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
              className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)]"
            />
          </label>
          <label className="mt-3 block">
            <span className="mb-1 block text-[11px] font-medium text-[var(--color-text-dim)]">
              Confirmer le mot de passe
            </span>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
              className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)]"
            />
          </label>
          {error && (
            <div className="mt-3 rounded-xl border border-red-200 bg-[var(--color-danger-bg)] px-3 py-2 text-[11.5px] text-[var(--color-danger)]">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--color-accent)] px-4 py-2.5 text-[13px] font-semibold text-white shadow-[var(--shadow-soft)] transition-all hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Définir le mot de passe
          </button>
          <button
            type="button"
            onClick={onSignOut}
            className="mt-3 block w-full text-center text-[12px] text-[var(--color-text-dim)] transition-colors hover:text-[var(--color-text)]"
          >
            Se déconnecter
          </button>
        </form>
      </div>
    </div>
  )
}

interface BoardProps {
  user: User
  onSignOut: () => void
}

function Board({ user, onSignOut }: BoardProps) {
  const notify = useNotify()
  const confirm = useConfirm()
  const showUndoToast = useUndoToast()
  const [projects, setProjects] = useState<Project[]>([])
  const [folders, setFolders] = useState<Folder[]>([])
  const [lists, setLists] = useState<List[]>([])
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [profiles, setProfiles] = useState<UserProfile[]>([])
  // Project members (excluding the owner — they always have access). Loaded
  // for the selected project so the card menu / detail modal can list who
  // can be subscribed.
  const [projectMembers, setProjectMembers] = useState<ProjectMember[]>([])
  // Subscribers for every ticket in the current project. Map: ticket_id -> Set<user_id>.
  const [subsByTicket, setSubsByTicket] = useState<Map<string, Set<string>>>(new Map())
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [openTicketId, setOpenTicketId] = useState<string | null>(null)
  // True while a card is being dragged on the board — lets the sidebar
  // grey out channels a card can't be dropped on.
  const [cardDragActive, setCardDragActive] = useState(false)
  // Pending two-card merge (dragged a card and dwelled ~5s over another).
  // `survivorId` = the older card (survives + absorbs); `absorbedId` = the
  // newer card (folded into the survivor, then deleted). Drives the modal.
  const [mergeReq, setMergeReq] = useState<{
    survivorId: string
    absorbedId: string
  } | null>(null)
  const [mergeBusy, setMergeBusy] = useState(false)
  // Ticket the user wants to move across projects — drives the
  // MoveCardDialog modal. Null = closed.
  const [moveTicketId, setMoveTicketId] = useState<string | null>(null)
  // Ticket the user wants to reply to (« chantier réponse » MVP).
  // Drives the ReplyDialog modal. Null = closed.
  const [replyTicketId, setReplyTicketId] = useState<string | null>(null)
  // Cards whose card-level audio transcription is currently in flight.
  // Drives a small "Transcription en cours…" chip on the card so the user
  // gets feedback that the AI is working (transcribe-audio can take a few
  // seconds — without it the kebab menu just closes and nothing seems to
  // happen until the title/description suddenly change).
  const [transcribingTicketIds, setTranscribingTicketIds] = useState<
    Set<string>
  >(new Set())
  // Toggle the card-level "analysing audio" flag (drives the board chip AND
  // the detail-modal banner) — used by the modal's transcription paths so the
  // feedback is consistent everywhere, not just for the kebab action.
  const setTicketTranscribing = useCallback((id: string, on: boolean) => {
    setTranscribingTicketIds((prev) => {
      const next = new Set(prev)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })
  }, [])
  const [profileOpen, setProfileOpen] = useState(false)
  const [archivesOpen, setArchivesOpen] = useState(false)
  const [membersOpen, setMembersOpen] = useState(false)
  const [settingsProjectId, setSettingsProjectId] = useState<string | null>(null)
  const [inviteCardsProjectId, setInviteCardsProjectId] = useState<string | null>(null)
  const [showArchived, setShowArchived] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  // "Favoris uniquement" filter — when true, the board hides any card that
  // isn't flagged as favorite. Persists across project switches because users
  // typically want to scope their attention to favorites until they say otherwise.
  const [favoritesOnly, setFavoritesOnly] = useState(false)
  // "Awaiting reply" filter — restricts to conversations where the contact
  // spoke last and we still owe a reply (tickets.awaiting_reply). Ephemeral.
  const [awaitingOnly, setAwaitingOnly] = useState(false)
  // AI signal filters — combinable via AND. Each toggle restricts the
  // visible cards to those that have the corresponding AI flag set
  // (ai_is_question / ai_mentioned_date / ai_is_urgent). Lives in
  // ephemeral state (NOT persisted): users typically flick filters on
  // for a triage burst, not as a permanent setting.
  const [aiFilters, setAiFilters] = useState<{
    question: boolean
    date: boolean
    urgent: boolean
  }>({ question: false, date: false, urgent: false })
  const toggleAiFilter = useCallback(
    (key: 'question' | 'date' | 'urgent') => {
      setAiFilters((prev) => ({ ...prev, [key]: !prev[key] }))
    },
    [],
  )
  // Label filter — multi-select set of labels the user has picked from the
  // topbar dropdown. A card matches when it carries ALL the selected
  // labels (AND, since the user explicitly wanted to "cumuler" them — pile
  // restrictions to narrow down). Reset whenever the project changes
  // because labels are project-scoped: selecting « Bug » on project A
  // means nothing on project B.
  const [labelFilters, setLabelFilters] = useState<Set<string>>(new Set())
  const toggleLabelFilter = useCallback((label: string) => {
    setLabelFilters((prev) => {
      const next = new Set(prev)
      if (next.has(label)) next.delete(label)
      else next.add(label)
      return next
    })
  }, [])
  const clearLabelFilters = useCallback(() => {
    setLabelFilters(new Set())
  }, [])
  // Temporal filter — "how long has the card been here". Anchored on each
  // card's arrival time (source_date ?? created_at). Ephemeral (like the
  // AI filters): a triage lens, not a persisted setting. Default = all time.
  const [timeFilter, setTimeFilter] = useState<TimeFilter>(ALL_TIME)
  // Labels are project-scoped (a « Bug » label on project A is not the same
  // as a « Bug » label on project B, even when spelled the same). Switching
  // projects with an active label filter would silently filter out
  // everything → drop the filter when the project changes.
  useEffect(() => {
    setLabelFilters(new Set())
  }, [selectedId])
  // List view — which list "categories" are HIDDEN in the mail-style stack.
  // Lists act as toggleable tags there; we track the hidden ones (default
  // none → all shown) so we never need to know the full list set up front.
  // Reset on project change so categories reflect the current project.
  const [hiddenListIds, setHiddenListIds] = useState<Set<string>>(new Set())
  useEffect(() => {
    setHiddenListIds(new Set())
  }, [selectedId])
  const toggleShownList = useCallback((listId: string) => {
    setHiddenListIds((prev) => {
      const next = new Set(prev)
      if (next.has(listId)) next.delete(listId)
      else next.add(listId)
      return next
    })
  }, [])
  const showAllLists = useCallback(() => setHiddenListIds(new Set()), [])
  // Cards mid fade-out (archived from the board) — kept rendered until the
  // exit animation finishes.
  const [exitingIds, setExitingIds] = useState<Set<string>>(new Set())
  // Cards whose one-shot "drop in" animation is currently playing. Populated
  // on realtime INSERT echoes + on local inserts (createCard, applyCardSplit).
  const [enteringIds, setEnteringIds] = useState<Set<string>>(new Set())
  // Projects currently playing the « new message » bounce (transient).
  const [bouncingProjectIds, setBouncingProjectIds] = useState<Set<string>>(
    new Set(),
  )
  // Timestamp of the previous visit — cards created after this are "new".
  // Captured once on load, frozen for the session.
  // Per-card "seen" markers for THIS user (ticket_id → seen_at ISO). A card is
  // "new" until it's opened or previewed (which upserts a marker) — within the
  // configurable « new for X days » window. Loaded once, kept live.
  const [cardViews, setCardViews] = useState<Map<string, string>>(new Map())
  const [loading, setLoading] = useState(true)
  // Fullscreen media viewer (opened from a card's media chip)
  const [lightboxUrls, setLightboxUrls] = useState<string[] | null>(null)
  // Compact audio panel — opens when an audio chip on a card is tapped.
  const [audioPanel, setAudioPanel] = useState<
    { urls: string[]; ticket: Ticket } | null
  >(null)
  // Hover preview popover
  const [hover, setHover] = useState<{ ticket: Ticket; rect: DOMRect } | null>(null)
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    let cancelled = false

    async function load() {
      const [
        { data: p },
        { data: l },
        { data: t },
        { data: pr },
        { data: pm },
        { data: subs },
        { data: fld },
      ] = await Promise.all([
        supabase
          .from('projects')
          .select('*')
          .order('position', { ascending: true })
          .order('created_at', { ascending: true }),
        supabase.from('lists').select('*').order('position', { ascending: true }),
        supabase.from('tickets').select('*').order('position', { ascending: true }),
        supabase
          .from('profiles')
          .select('*'),
        supabase.from('project_members').select('*'),
        supabase.from('ticket_subscribers').select('ticket_id, user_id'),
        supabase
          .from('project_folders')
          .select('*')
          .order('position', { ascending: true })
          .order('created_at', { ascending: true }),
      ])
      if (cancelled) return
      setProjects(p ?? [])
      setFolders(fld ?? [])
      setLists(l ?? [])
      setTickets(t ?? [])
      setProfiles(pr ?? [])
      setProjectMembers(pm ?? [])
      // Rebuild the ticket_id → Set<user_id> map from the flat rows.
      const map = new Map<string, Set<string>>()
      for (const s of subs ?? []) {
        const set = map.get(s.ticket_id) ?? new Set<string>()
        set.add(s.user_id)
        map.set(s.ticket_id, set)
      }
      setSubsByTicket(map)
      if (!selectedId && p && p.length > 0) {
        // Restore the last-visited project for this user if it still
        // exists (shared projects can disappear if a collaborator was
        // revoked or the owner deleted it), otherwise fall back to the
        // first project in the sidebar order.
        const remembered = lastProjectIdFor(user.id)
        const valid = remembered && p.some((x) => x.id === remembered)
        setSelectedId(valid ? remembered : p[0].id)
      }
      setLoading(false)

      // Heartbeat: stamp "now" as the last visit (still useful for presence /
      // pending-invite detection). "New" cards are now tracked per-card via
      // card_views, not this global timestamp.
      supabase
        .from('profiles')
        .update({ last_seen_at: new Date().toISOString() })
        .eq('id', user.id)
        .then(({ error }) => {
          if (error) console.error('last_seen update failed', error)
        })
    }
    load()

    const channel = supabase
      .channel('whatskan-live')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'projects' },
        (payload) => {
          const p = payload as unknown as RealtimePayload<Project>
          setProjects((prev) => applyChange(prev, p))
          // Auto-switch to a brand-new project so the user sees the ticket arrive
          if (p.eventType === 'INSERT') setSelectedId(p.new.id)
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'lists' },
        (payload) => {
          const l = payload as unknown as RealtimePayload<List>
          setLists((prev) => applyChange(prev, l))
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'profiles' },
        (payload) => {
          const pr = payload as unknown as RealtimePayload<UserProfile>
          setProfiles((prev) => applyChange(prev, pr))
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tickets' },
        (payload) => {
          const t = payload as unknown as RealtimePayload<Ticket>
          setTickets((prev) => applyChange(prev, t))
          if (t.eventType === 'INSERT') {
            // Play the one-shot entrance animation. Safe to call even for
            // locally-inserted cards (markEntering is idempotent).
            markEntering(t.new.id)
            // Inbound card (created by the WhatsApp ingest = no auth.uid()):
            // bounce the project in the sidebar + play its notification
            // sound (when the channel has one AND the user master is on).
            // Manually-created cards (created_by = the user) don't ping.
            if (t.new.created_by == null) {
              markProjectBounce(t.new.project_id)
              const proj = projectsRef.current.find(
                (p) => p.id === t.new.project_id,
              )
              const masterOn =
                userPrefsRef.current.notify_sound_enabled !== false
              if (proj && masterOn && proj.notify_sound && proj.notify_sound !== 'off') {
                playNotifySound(proj.notify_sound)
              }
              // Auto-transcribe a freshly-arrived voice note OR video (pref
              // on) so its transcription + detected date/urgency/question
              // land WITHOUT the user having to open the card. Whisper
              // handles video containers natively (it extracts the audio
              // track), so we feed the same edge function with either kind.
              if (userPrefsRef.current.auto_transcribe === true) {
                const transcribeUrl = (t.new.media_urls ?? []).find((u) =>
                  /\.(ogg|oga|opus|mp3|m4a|aac|wav|amr|mp4|m4v|mov|webm|3gp)(\?|$)/i.test(u),
                )
                const already = (t.new.transcribed_audio_urls ?? []).includes(
                  transcribeUrl ?? '',
                )
                if (transcribeUrl && !already) {
                  void transcribeCardAudio(t.new, transcribeUrl)
                }
              }
            }
            // If a ticket lands on a project we're not looking at, switch to it.
            setSelectedId((cur) => (cur === t.new.project_id ? cur : t.new.project_id))
          } else if (t.eventType === 'UPDATE') {
            // A contact reply was attached to an existing conversation card
            // (last_reply_at bumped) → ping the channel like a new inbound.
            const prevT = ticketsRef.current.find((x) => x.id === t.new.id)
            const newReply = t.new.last_reply_at ?? null
            if (newReply && newReply !== (prevT?.last_reply_at ?? null)) {
              markProjectBounce(t.new.project_id)
              const proj = projectsRef.current.find(
                (p) => p.id === t.new.project_id,
              )
              const masterOn =
                userPrefsRef.current.notify_sound_enabled !== false
              if (
                proj &&
                masterOn &&
                proj.notify_sound &&
                proj.notify_sound !== 'off'
              ) {
                playNotifySound(proj.notify_sound)
              }
            }
          }
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'project_members' },
        async (payload) => {
          const m = payload as unknown as RealtimePayload<ProjectMember>
          // A project was just shared with me — pull it into view.
          if (m.eventType === 'INSERT' && m.new.user_id === user.id) {
            const pid = m.new.project_id
            const [{ data: proj }, { data: l }, { data: t }] = await Promise.all([
              supabase.from('projects').select('*').eq('id', pid).maybeSingle(),
              supabase
                .from('lists')
                .select('*')
                .eq('project_id', pid)
                .order('position', { ascending: true }),
              supabase
                .from('tickets')
                .select('*')
                .eq('project_id', pid)
                .order('position', { ascending: true }),
            ])
            if (cancelled) return
            if (proj)
              setProjects((prev) =>
                prev.some((x) => x.id === pid) ? prev : [...prev, proj as Project],
              )
            if (l)
              setLists((prev) => [
                ...prev.filter((x) => x.project_id !== pid),
                ...(l as List[]),
              ])
            if (t)
              setTickets((prev) => [
                ...prev.filter((x) => x.project_id !== pid),
                ...(t as Ticket[]),
              ])
          }
          // I was removed from a project — drop it from view.
          if (m.eventType === 'DELETE') {
            const old = m.old as Partial<ProjectMember>
            if (old.user_id === user.id && old.project_id) {
              const pid = old.project_id
              setProjects((prev) => prev.filter((x) => x.id !== pid))
              setLists((prev) => prev.filter((x) => x.project_id !== pid))
              setTickets((prev) => prev.filter((x) => x.project_id !== pid))
              setSelectedId((cur) => (cur === pid ? null : cur))
            }
          }
          // Keep the local project_members list in sync too (used by the card
          // subscriber picker).
          if (m.eventType === 'INSERT')
            setProjectMembers((prev) =>
              prev.some(
                (x) => x.project_id === m.new.project_id && x.user_id === m.new.user_id,
              )
                ? prev
                : [...prev, m.new as ProjectMember],
            )
          if (m.eventType === 'DELETE') {
            const old = m.old as Partial<ProjectMember>
            setProjectMembers((prev) =>
              prev.filter(
                (x) => !(x.project_id === old.project_id && x.user_id === old.user_id),
              ),
            )
          }
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'ticket_subscribers' },
        (payload) => {
          const s = payload as unknown as RealtimePayload<{
            ticket_id: string
            user_id: string
          }>
          setSubsByTicket((prev) => {
            const next = new Map(prev)
            if (s.eventType === 'INSERT') {
              const set = new Set(next.get(s.new.ticket_id) ?? [])
              set.add(s.new.user_id)
              next.set(s.new.ticket_id, set)
            } else if (s.eventType === 'DELETE') {
              const old = s.old as { ticket_id?: string; user_id?: string }
              if (!old.ticket_id || !old.user_id) return prev
              const set = new Set(next.get(old.ticket_id) ?? [])
              set.delete(old.user_id)
              if (set.size === 0) next.delete(old.ticket_id)
              else next.set(old.ticket_id, set)
            }
            return next
          })
        },
      )
      .subscribe()

    return () => {
      cancelled = true
      supabase.removeChannel(channel)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Persist the active project per user, so the next page load lands
  // back on it instead of always snapping to the first project.
  useEffect(() => {
    if (loading) return
    if (selectedId) setLastProjectIdFor(user.id, selectedId)
  }, [selectedId, user.id, loading])

  // Focus-refetch safety net — Realtime WS connections sometimes drop on tabs
  // that fell asleep (mobile background, OS power saving…). When the tab
  // regains focus, re-pull the small reference tables (profiles, members,
  // subscribers) so stale avatars / names / abos can't linger.
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState !== 'visible') return
      Promise.all([
        supabase
          .from('profiles')
          .select('*'),
        supabase.from('project_members').select('*'),
        supabase.from('ticket_subscribers').select('ticket_id, user_id'),
      ]).then(([{ data: pr }, { data: pm }, { data: subs }]) => {
        if (pr) setProfiles(pr)
        if (pm) setProjectMembers(pm)
        if (subs) {
          const map = new Map<string, Set<string>>()
          for (const s of subs) {
            const set = map.get(s.ticket_id) ?? new Set<string>()
            set.add(s.user_id)
            map.set(s.ticket_id, set)
          }
          setSubsByTicket(map)
        }
      })
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])

  // Polling fallback — Realtime postgres_changes can stall when the WAL holds
  // rows tagged with a dropped type. Delta poll (gt updated_at) every 8 s
  // keeps egress low: usually 0 rows come back. Every ~64 s a full reconcile
  // also catches deletions a collaborator may have made.
  useEffect(() => {
    if (loading) return
    let cancelled = false
    const lastSeen = { current: '' }
    let cycle = 0

    function bumpWatermark(rows: Ticket[]) {
      let max = lastSeen.current
      for (const t of rows) if (t.updated_at > max) max = t.updated_at
      lastSeen.current = max
    }

    async function pollDelta() {
      if (cancelled || document.hidden) return
      // Lazy-init the watermark from whatever we already have in memory.
      if (!lastSeen.current) {
        bumpWatermark(ticketsRef.current)
      }
      const { data, error } = await supabase
        .from('tickets')
        .select('*')
        .gt('updated_at', lastSeen.current || '1970-01-01T00:00:00Z')
      if (cancelled || error || !data || data.length === 0) return
      bumpWatermark(data as Ticket[])
      setTickets((prev) => {
        const byId = new Map(prev.map((x) => [x.id, x]))
        for (const fresh of data as Ticket[]) {
          const local = byId.get(fresh.id)
          // Local optimistic copy is newer — keep it for now.
          if (local && local.updated_at > fresh.updated_at) continue
          byId.set(fresh.id, fresh)
        }
        return Array.from(byId.values())
      })
    }

    async function pollFull() {
      if (cancelled || document.hidden) return
      const { data, error } = await supabase
        .from('tickets')
        .select('*')
        .order('position', { ascending: true })
      if (cancelled || error || !data) return
      bumpWatermark(data as Ticket[])
      setTickets((prev) => {
        const localById = new Map(prev.map((x) => [x.id, x]))
        return (data as Ticket[]).map((fresh) => {
          const local = localById.get(fresh.id)
          if (local && local.updated_at > fresh.updated_at) return local
          return fresh
        })
      })
    }

    async function pollEverything() {
      if (cancelled || document.hidden) return
      const [{ data: p }, { data: l }] = await Promise.all([
        supabase
          .from('projects')
          .select('*')
          .order('created_at', { ascending: true }),
        supabase
          .from('lists')
          .select('*')
          .order('position', { ascending: true }),
      ])
      if (cancelled) return
      if (p) setProjects(p as Project[])
      if (l) setLists(l as List[])
      await pollFull()
    }

    async function tick() {
      if (cancelled || document.hidden) return
      cycle += 1
      // Every 8th cycle (~64 s) do a full reconcile so collaborator deletions
      // eventually disappear too. The rest are cheap delta queries.
      if (cycle % 8 === 0) await pollFull()
      else await pollDelta()
    }

    const intervalId = setInterval(tick, 8000)
    function onVisibility() {
      if (!document.hidden) pollEverything()
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      cancelled = true
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [loading])

  // list_id -> is_done, so we can count "open" tickets (not in a done list)
  const doneListIds = useMemo(
    () => new Set(lists.filter((l) => l.is_done).map((l) => l.id)),
    [lists],
  )

  const ticketCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const t of tickets) {
      if (!t.archived && !doneListIds.has(t.list_id)) {
        counts[t.project_id] = (counts[t.project_id] ?? 0) + 1
      }
    }
    return counts
  }, [tickets, doneListIds])

  // ── "New" cards (per user, per card) ──────────────────────────────────
  // A card is NEW while: (1) the user hasn't opened/previewed it since its
  // latest activity (no card_views marker, or activity is newer than the
  // marker), AND (2) that activity is within the « new for X days » window
  // (pref new_card_days, default 7). Opening or hovering a card clears it.
  const newCardDays = Math.max(
    1,
    ((profiles.find((p) => p.id === user.id)?.preferences as
      | UserPreferences
      | undefined)?.new_card_days) ?? 7,
  )
  const newTicketIds = useMemo(() => {
    const ids = new Set<string>()
    const windowMs = newCardDays * 86_400_000
    const now = Date.now()
    for (const t of tickets) {
      if (t.archived) continue
      const activityIso =
        t.last_reply_at && t.last_reply_at > (t.source_date ?? t.created_at)
          ? t.last_reply_at
          : t.source_date ?? t.created_at
      const activityMs = new Date(activityIso).getTime()
      if (now - activityMs >= windowMs) continue // outside the "new" window
      const seen = cardViews.get(t.id)
      if (seen && new Date(seen).getTime() >= activityMs) continue // already seen
      ids.add(t.id)
    }
    return ids
  }, [tickets, cardViews, newCardDays])

  // Per-project count of new cards (sidebar "+N" badge + favicon total).
  const newCountByProject = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const t of tickets) {
      if (newTicketIds.has(t.id)) {
        counts[t.project_id] = (counts[t.project_id] ?? 0) + 1
      }
    }
    return counts
  }, [tickets, newTicketIds])

  // Browser tab title — prefix with the total count of new messages (across
  // all channels) as a « (N) » badge, e.g. « (3) msgBan ». Clears to plain
  // « msgBan » when nothing's new.
  useEffect(() => {
    const total = Object.values(newCountByProject).reduce((a, b) => a + b, 0)
    // Title stays clean — the count lives only in the favicon badge below.
    document.title = 'msgBan'
    // Red count bubble drawn over the favicon (native-app-style tab badge).
    setFaviconBadge(total)
  }, [newCountByProject])

  // Re-sync channels when the tab regains focus/visibility. Realtime delivers
  // live changes (e.g. a channel rename by the owner), but if the tab was
  // asleep when the event fired a member could be left on a stale name — this
  // self-heals it from the DB (source of truth) without a full reload.
  useEffect(() => {
    function resync() {
      if (document.visibilityState !== 'visible') return
      supabase
        .from('projects')
        .select('*')
        .order('created_at', { ascending: true })
        .then(({ data }) => {
          if (data) setProjects(data as Project[])
        })
    }
    document.addEventListener('visibilitychange', resync)
    window.addEventListener('focus', resync)
    return () => {
      document.removeEventListener('visibilitychange', resync)
      window.removeEventListener('focus', resync)
    }
  }, [])

  // Load this user's per-card "seen" markers once.
  useEffect(() => {
    let cancelled = false
    supabase
      .from('card_views')
      .select('ticket_id, seen_at')
      .eq('user_id', user.id)
      .then(({ data }) => {
        if (cancelled || !data) return
        setCardViews(
          new Map(
            (data as { ticket_id: string; seen_at: string }[]).map((r) => [
              r.ticket_id,
              r.seen_at,
            ]),
          ),
        )
      })
    return () => {
      cancelled = true
    }
  }, [user.id])

  // Mark a card as seen (opened or previewed) for this user — optimistic local
  // update + upsert. Clears its "new" state.
  const markCardSeen = useCallback(
    (ticketId: string) => {
      const now = new Date().toISOString()
      // Skip if already marked at/after the card's latest activity (cheap).
      setCardViews((prev) => {
        const next = new Map(prev)
        next.set(ticketId, now)
        return next
      })
      // NB: supabase-js query builders are lazy — the request only fires when
      // the thenable is awaited / .then()'d. A bare `void supabase…` never
      // executes, so the seen marker would never persist (revives on reload).
      supabase
        .from('card_views')
        .upsert(
          { user_id: user.id, ticket_id: ticketId, seen_at: now },
          { onConflict: 'user_id,ticket_id' },
        )
        .then(({ error }) => {
          if (error) console.error('card_views upsert failed', error)
        })
    },
    [user.id],
  )

  // Projects that currently have at least one active urgent card — drives
  // the flame icon next to the project in the sidebar. "Urgent" = either
  // the AI flagged it (ai_is_urgent) OR its priority is set to 'urgent'
  // (manual or auto-bumped). The flame stays as long as ANY active card
  // qualifies, and disappears once they're all done/archived/de-prioritised.
  // Flame colour per project: 'red' if it has an active URGENT card
  // (ai_is_urgent OR priority 'urgent'), else 'orange' if it has an active
  // HIGH-priority card. Urgent always wins over high.
  const flameByProject = useMemo(() => {
    const map = new Map<string, 'red' | 'orange'>()
    for (const t of tickets) {
      if (t.archived) continue
      if (t.ai_is_urgent || t.priority === 'urgent') {
        map.set(t.project_id, 'red')
      } else if (t.priority === 'high' && map.get(t.project_id) !== 'red') {
        map.set(t.project_id, 'orange')
      }
    }
    return map
  }, [tickets])

  // Active (non-archived) cards of the selected project
  const visibleTickets = useMemo(
    () => tickets.filter((t) => t.project_id === selectedId && !t.archived),
    [tickets, selectedId],
  )

  // Number of favorited cards in the selected project — drives a small badge
  // next to the « Favoris » button so users see at a glance how many cards
  // they'd see if they enabled the filter.
  const favoriteCount = useMemo(
    () => visibleTickets.filter((t) => t.favorite).length,
    [visibleTickets],
  )

  // Number of cards awaiting a reply in the selected project — badge next
  // to the « À répondre » filter.
  const awaitingCount = useMemo(
    () => visibleTickets.filter((t) => t.awaiting_reply).length,
    [visibleTickets],
  )

  // Per-AI-signal counts in the current project, surfaced as small
  // badges next to each filter button so the user can see what's in
  // play before toggling it on.
  const aiFilterCounts = useMemo(
    () => ({
      question: visibleTickets.filter((t) => t.ai_is_question).length,
      date: visibleTickets.filter((t) => !!t.ai_mentioned_date).length,
      // The flame filter covers BOTH urgent (red) and high (orange) —
      // any card the flame icon would light up for.
      urgent: visibleTickets.filter(
        (t) => isTicketUrgent(t) || t.priority === 'high',
      ).length,
    }),
    [visibleTickets],
  )

  // Search filter: when a query is set, the board shows only matching cards.
  // Favorites toggle and AI filters layer on top — all filters combine
  // AND-style (only cards matching ALL active filters survive).
  const searchActive =
    searchQuery.trim().length > 0 || labelFilters.size > 0
  const matchedTickets = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    const now = Date.now()
    let out = visibleTickets
    if (favoritesOnly) out = out.filter((t) => t.favorite)
    if (awaitingOnly) out = out.filter((t) => t.awaiting_reply)
    if (aiFilters.question) out = out.filter((t) => t.ai_is_question)
    if (aiFilters.date) out = out.filter((t) => !!t.ai_mentioned_date)
    if (aiFilters.urgent)
      out = out.filter((t) => isTicketUrgent(t) || t.priority === 'high')
    if (timeFilter.kind !== 'all')
      out = out.filter((t) =>
        matchesTimeFilter(timeFilter, t.source_date ?? t.created_at, now),
      )
    if (labelFilters.size > 0) {
      // Cumulative (OR): a card passes if it carries AT LEAST ONE of the
      // selected labels (case-insensitive). Selecting « SMA » + « SMD »
      // surfaces every card tagged SMA or SMD, not only those with both.
      const wanted = [...labelFilters].map((l) => l.toLowerCase())
      out = out.filter((t) => {
        const labs = (t.labels ?? []).map((l) => l.toLowerCase())
        return wanted.some((w) => labs.includes(w))
      })
    }
    if (q)
      out = out.filter(
        (t) =>
          t.title.toLowerCase().includes(q) ||
          (t.description?.toLowerCase().includes(q) ?? false) ||
          // Free-text search also matches labels, so typing a label name
          // in the search bar narrows by that label (point 1: « recherche
          // par étiquette »), complementing the multi-select dropdown.
          (t.labels ?? []).some((l) => l.toLowerCase().includes(q)),
      )
    return out
  }, [
    visibleTickets,
    searchQuery,
    favoritesOnly,
    awaitingOnly,
    aiFilters,
    timeFilter,
    labelFilters,
  ])

  // Archived cards of the selected project (for the archives modal)
  const archivedTickets = useMemo(
    () =>
      tickets
        .filter((t) => t.project_id === selectedId && t.archived)
        .sort((a, b) => (b.source_date ?? b.created_at).localeCompare(a.source_date ?? a.created_at)),
    [tickets, selectedId],
  )

  // How many archived cards match the active search — drives the topbar badge.
  const matchedArchivedCount = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return 0
    return archivedTickets.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        (t.description?.toLowerCase().includes(q) ?? false) ||
        t.labels.some((l) => l.toLowerCase().includes(q)),
    ).length
  }, [archivedTickets, searchQuery])

  const visibleLists = useMemo(
    () =>
      lists
        .filter((l) => l.project_id === selectedId)
        .sort((a, b) => a.position - b.position),
    [lists, selectedId],
  )

  // Set of list ids currently SHOWN in the list view (= not hidden).
  const shownListIdSet = useMemo(
    () =>
      new Set(
        visibleLists.filter((l) => !hiddenListIds.has(l.id)).map((l) => l.id),
      ),
    [visibleLists, hiddenListIds],
  )

  // Folders sorted by position so a reorder (which only updates the
  // `position` field, not the array order) is reflected immediately.
  const sortedFolders = useMemo(
    () =>
      [...folders].sort(
        (a, b) =>
          a.position - b.position || a.created_at.localeCompare(b.created_at),
      ),
    [folders],
  )

  const visibleProjects = useMemo(
    () =>
      projects
        .filter((p) => (showArchived ? true : !p.archived))
        // Local sort by position so a Realtime UPDATE (or a reorder echo)
        // doesn't leave the sidebar in its previous order.
        .sort((a, b) =>
          a.position - b.position ||
          a.created_at.localeCompare(b.created_at),
        ),
    [projects, showArchived],
  )

  const archivedCount = useMemo(
    () => projects.filter((p) => p.archived).length,
    [projects],
  )

  const profilesById = useMemo(() => {
    const map: Record<string, UserProfile> = {}
    for (const p of profiles) map[p.id] = p
    return map
  }, [profiles])

  // ── Per-user preferences (theme, auto-transcribe…) ────────────────────
  const userPrefs: UserPreferences = useMemo(() => {
    return (profilesById[user.id]?.preferences as UserPreferences) ?? {}
  }, [profilesById, user.id])

  // Board layout (kanban columns vs mail-style list) — persisted per-user.
  const boardView: 'kanban' | 'list' =
    userPrefs.board_view === 'list' ? 'list' : 'kanban'

  useEffect(() => {
    const theme = isThemeKey(userPrefs.theme) ? userPrefs.theme : DEFAULT_THEME
    document.documentElement.dataset.theme = theme
  }, [userPrefs.theme])

  // Keep the i18next instance in sync with the user's saved locale.
  // Without this, the language detector (localStorage / navigator) would
  // win on every reload — the user's explicit choice from the Profile
  // modal would only stick until next refresh. We force-push it here.
  useEffect(() => {
    const target = resolveLocale(userPrefs.locale)
    if (i18n.language !== target) {
      void i18n.changeLanguage(target)
    }
    // Also keep <html lang="..."> in sync so screen readers, font
    // hinting, and browser features (spellcheck) get the right hint.
    document.documentElement.lang = target
  }, [userPrefs.locale])

  const updatePreferences = useCallback(
    async (patch: Partial<UserPreferences>) => {
      const prev = profilesById[user.id]
      const merged: UserPreferences = { ...(prev?.preferences ?? {}), ...patch }
      // Optimistic update so the theme/checkbox feels instant.
      setProfiles((arr) =>
        arr.map((p) => (p.id === user.id ? { ...p, preferences: merged } : p)),
      )
      const { error } = await supabase
        .from('profiles')
        .update({ preferences: merged })
        .eq('id', user.id)
      if (error) {
        console.error('updatePreferences failed, rolling back:', error)
        setProfiles((arr) => arr.map((p) => (p.id === user.id && prev ? prev : p)))
      }
    },
    [profilesById, user.id],
  )

  // Generic profile-row updater for the Profile / Company / WhatsApp /
  // Billing tabs of the account modal. Takes any subset of UserProfile
  // columns, does the optimistic local patch, persists to Supabase, and
  // rolls back on error (mirrors updatePreferences's strategy). Returns
  // the supabase error message in the thrown error so the tab UI can
  // surface it inline.
  const updateProfileFields = useCallback(
    async (patch: Partial<UserProfile>) => {
      const prev = profilesById[user.id]
      const merged: UserProfile = {
        ...(prev as UserProfile),
        ...patch,
      }
      setProfiles((arr) =>
        arr.map((p) => (p.id === user.id ? merged : p)),
      )
      const { error } = await supabase
        .from('profiles')
        .update(patch)
        .eq('id', user.id)
      if (error) {
        console.error('updateProfileFields failed, rolling back:', error)
        setProfiles((arr) => arr.map((p) => (p.id === user.id && prev ? prev : p)))
        throw new Error(error.message)
      }
    },
    [profilesById, user.id],
  )

  // Per-list compact toggle (saved in profiles.preferences.compact_lists).
  // Backward-compat: if legacy `compact_cards` was true, treat every list as
  // compact by default until the user explicitly toggles one off.
  const compactListIds = useMemo(() => {
    return new Set(userPrefs.compact_lists ?? [])
  }, [userPrefs.compact_lists])
  const toggleListCompact = useCallback(
    (listId: string) => {
      const cur = new Set(userPrefs.compact_lists ?? [])
      if (cur.has(listId)) cur.delete(listId)
      else cur.add(listId)
      updatePreferences({ compact_lists: Array.from(cur) })
    },
    [userPrefs.compact_lists, updatePreferences],
  )

  // Global audio playback rate. Shared by every InlineAudio across the
  // board AND the detail modal, so the user only picks 1.5× once and it
  // sticks for every audio they listen to next, across reloads.
  const audioSpeed = useMemo<AudioSpeed>(() => {
    const v = userPrefs.audio_speed
    return (AUDIO_SPEEDS as readonly number[]).includes(v ?? -1)
      ? (v as AudioSpeed)
      : DEFAULT_AUDIO_SPEED
  }, [userPrefs.audio_speed])
  const setAudioSpeed = useCallback(
    (rate: AudioSpeed) => {
      updatePreferences({ audio_speed: rate })
    },
    [updatePreferences],
  )

  const selected = projects.find((p) => p.id === selectedId)

  // Optimistic update: apply patch locally first, then write to DB. If the DB
  // call fails, revert. Realtime echoes will be no-ops because state already matches.
  const ticketsRef = useRef(tickets)
  ticketsRef.current = tickets
  // Mirror of userPrefs for callbacks that read prefs without depending
  // on the prefs object identity (avoids invalidating useCallback every
  // time the user flips an unrelated toggle).
  const userPrefsRef = useRef(userPrefs)
  userPrefsRef.current = userPrefs
  const projectsRef = useRef(projects)
  projectsRef.current = projects
  // Needed by moveCardToProject (cross-project move) to compute who in
  // the destination project will keep / lose subscription access.
  const projectMembersRef = useRef(projectMembers)
  projectMembersRef.current = projectMembers
  const subsByTicketRef = useRef(subsByTicket)
  subsByTicketRef.current = subsByTicket
  const listsRef = useRef(lists)
  listsRef.current = lists
  const foldersRef = useRef(folders)
  foldersRef.current = folders

  const updateTicket = useCallback(
    async (id: string, patch: Partial<Ticket>) => {
      const prev = ticketsRef.current.find((t) => t.id === id)
      if (!prev) return
      const optimistic: Ticket = { ...prev, ...patch, updated_at: new Date().toISOString() }
      setTickets((list) => list.map((t) => (t.id === id ? optimistic : t)))

      const { error } = await supabase.from('tickets').update(patch).eq('id', id)
      if (error) {
        console.error('Ticket update failed, rolling back:', error)
        setTickets((list) => list.map((t) => (t.id === id ? prev : t)))
        return
      }

      // Notification fan-out (best-effort). We compare prev vs patch and
      // fire the relevant event AFTER the DB write returns OK. Each event
      // is independent — a card move + archive in the same patch would
      // emit two notifications, which is the right behaviour.
      if (patch.list_id !== undefined && patch.list_id !== prev.list_id) {
        void notifyEvent({
          type: 'card_status',
          ticket_id: id,
          from_list_id: prev.list_id,
          to_list_id: patch.list_id as string,
        })
      }
      if (patch.archived === true && prev.archived !== true) {
        void notifyEvent({ type: 'card_archived', ticket_id: id })
      } else if (patch.archived === false && prev.archived === true) {
        void notifyEvent({ type: 'card_restored', ticket_id: id })
      }
      if (patch.priority !== undefined && patch.priority !== prev.priority) {
        void notifyEvent({
          type: 'card_priority',
          ticket_id: id,
          from_priority: prev.priority ?? null,
          to_priority: (patch.priority as string | null) ?? null,
        })
      }
    },
    [],
  )

  const deleteTicket = useCallback(async (id: string) => {
    const prev = ticketsRef.current
    const ticket = prev.find((t) => t.id === id)
    // Snapshot subscribers + title BEFORE the delete cascades them
    // away, so notify-event can still email them. Resolved from the
    // already-loaded subsByTicketRef rather than a fresh DB roundtrip.
    const subscribers = ticket
      ? Array.from(subsByTicketRef.current.get(id) ?? [])
      : []
    const snapshot = ticket
      ? {
          title: ticket.title ?? '',
          project_id: ticket.project_id,
          subscribers,
        }
      : null

    setTickets((list) => list.filter((t) => t.id !== id))
    const { error } = await supabase.from('tickets').delete().eq('id', id)
    if (error) {
      console.error('Ticket delete failed, rolling back:', error)
      setTickets(prev)
      return
    }
    if (snapshot) {
      void notifyEvent({
        type: 'card_deleted',
        ticket_id: id,
        project_id: snapshot.project_id,
        title: snapshot.title,
        subscriber_ids: snapshot.subscribers,
      })
    }
  }, [])

  /** Delete a card from the kebab menu with a 15 s undo window. The card
   *  vanishes optimistically; nothing is removed from the DB until the
   *  toast's grace window elapses — so « Annuler » is a pure local restore
   *  (no re-insert). On commit we run the real delete (DB + notify). */
  const deleteCardWithUndo = useCallback(
    (id: string) => {
      const ticket = ticketsRef.current.find((t) => t.id === id)
      if (!ticket) return
      const subscribers = Array.from(subsByTicketRef.current.get(id) ?? [])
      // Optimistic removal — keep the row so we can splice it back on undo.
      setTickets((list) => list.filter((t) => t.id !== id))
      if (openTicketId === id) setOpenTicketId(null)
      showUndoToast({
        message: i18n.t('card.deletedUndo', { title: ticket.title || '—' }),
        durationMs: 15000,
        onUndo: () => {
          // Restore the exact row (it never left the DB).
          setTickets((list) =>
            list.some((t) => t.id === id) ? list : [...list, ticket],
          )
        },
        onCommit: async () => {
          const { error } = await supabase.from('tickets').delete().eq('id', id)
          if (error) {
            console.error('Ticket delete failed, restoring:', error)
            setTickets((list) =>
              list.some((t) => t.id === id) ? list : [...list, ticket],
            )
            notify({
              title: i18n.t('card.deleteFailed'),
              message: error.message,
              tone: 'error',
            })
            return
          }
          void notifyEvent({
            type: 'card_deleted',
            ticket_id: id,
            project_id: ticket.project_id,
            title: ticket.title ?? '',
            subscriber_ids: subscribers,
          })
        },
      })
    },
    [showUndoToast, notify, openTicketId],
  )

  /** Open the merge confirmation modal for two cards. The OLDER card (by
   *  underlying message date, falling back to creation) survives and absorbs
   *  the other; the newer one is folded in then deleted. Called by the board
   *  when the user drags a card and dwells ~5s over another. */
  const requestMerge = useCallback((draggedId: string, targetId: string) => {
    if (draggedId === targetId) return
    const all = ticketsRef.current
    const a = all.find((t) => t.id === draggedId)
    const b = all.find((t) => t.id === targetId)
    if (!a || !b) return
    const timeOf = (t: Ticket) =>
      new Date(t.source_date ?? t.created_at).getTime()
    const survivor = timeOf(a) <= timeOf(b) ? a : b
    const absorbed = survivor.id === a.id ? b : a
    setMergeReq({ survivorId: survivor.id, absorbedId: absorbed.id })
  }, [])

  /** Apply a two-card merge: fold the absorbed card's content (description,
   *  messages, media, audio, labels, subscribers, signals) into the survivor,
   *  then delete the absorbed card. Non-destructive for the survivor's own
   *  identity (id, position, list, schedule are preserved). */
  const mergeTickets = useCallback(
    async (survivorId: string, absorbedId: string) => {
      const all = ticketsRef.current
      const survivor = all.find((t) => t.id === survivorId)
      const absorbed = all.find((t) => t.id === absorbedId)
      if (!survivor || !absorbed) return

      const uniq = (x?: string[] | null, y?: string[] | null) =>
        Array.from(new Set([...(x ?? []), ...(y ?? [])]))
      const later = (x: string | null, y: string | null) => {
        if (!x) return y
        if (!y) return x
        return new Date(x).getTime() >= new Date(y).getTime() ? x : y
      }

      // Description: survivor (older) first, then the absorbed card under a
      // divider — prefixed with its title when that adds information.
      const blocks: string[] = []
      if (survivor.description?.trim()) blocks.push(survivor.description.trim())
      const absBits: string[] = []
      const absTitle = absorbed.title?.trim()
      if (absTitle && absTitle !== survivor.title?.trim()) absBits.push(absTitle)
      if (absorbed.description?.trim()) absBits.push(absorbed.description.trim())
      if (absBits.length) blocks.push(absBits.join('\n'))
      const description =
        blocks.join('\n\n———\n\n') || survivor.description

      const patch: Partial<Ticket> = {
        description,
        source_message_ids: uniq(
          survivor.source_message_ids,
          absorbed.source_message_ids,
        ),
        media_urls: uniq(survivor.media_urls, absorbed.media_urls),
        transcribed_audio_urls: uniq(
          survivor.transcribed_audio_urls,
          absorbed.transcribed_audio_urls,
        ),
        labels: uniq(survivor.labels, absorbed.labels),
        audio_clips: [
          ...(survivor.audio_clips ?? []),
          ...(absorbed.audio_clips ?? []),
        ],
        favorite: survivor.favorite || absorbed.favorite,
        awaiting_reply: survivor.awaiting_reply || absorbed.awaiting_reply,
        priority: survivor.priority ?? absorbed.priority,
        assignee_id: survivor.assignee_id ?? absorbed.assignee_id,
        // Recency for the merged conversation = the newer of the two replies.
        last_reply_at: later(survivor.last_reply_at, absorbed.last_reply_at),
        // Scheduling / reminders: keep the survivor's if it has any, else
        // inherit the absorbed card's.
        scheduled_start: survivor.scheduled_start ?? absorbed.scheduled_start,
        scheduled_end: survivor.scheduled_end ?? absorbed.scheduled_end,
        scheduled_all_day: survivor.scheduled_start
          ? survivor.scheduled_all_day
          : absorbed.scheduled_all_day,
        ai_mentioned_date:
          survivor.ai_mentioned_date ?? absorbed.ai_mentioned_date,
        ai_is_question: survivor.ai_is_question || absorbed.ai_is_question,
        ai_is_urgent: survivor.ai_is_urgent || absorbed.ai_is_urgent,
        ai_confidence: survivor.ai_confidence ?? absorbed.ai_confidence,
      }

      // Union subscribers — add the absorbed card's followers the survivor
      // doesn't already have (the absorbed rows cascade away on delete).
      const survSubs = subsByTicketRef.current.get(survivorId) ?? new Set<string>()
      const absSubs = subsByTicketRef.current.get(absorbedId) ?? new Set<string>()
      const toAdd = Array.from(absSubs).filter((u) => !survSubs.has(u))

      await updateTicket(survivorId, patch)

      if (toAdd.length > 0) {
        const { error } = await supabase
          .from('ticket_subscribers')
          .upsert(
            toAdd.map((uid) => ({ ticket_id: survivorId, user_id: uid })),
            { onConflict: 'ticket_id,user_id' },
          )
        if (error) console.error('merge: subscriber union failed', error)
      }

      // Delete the absorbed card directly (no « card deleted » email — its
      // content lives on in the survivor, and its followers now follow that).
      const prev = ticketsRef.current
      setTickets((list) => list.filter((t) => t.id !== absorbedId))
      const { error: delErr } = await supabase
        .from('tickets')
        .delete()
        .eq('id', absorbedId)
      if (delErr) {
        console.error('merge: delete absorbed failed, rolling back', delErr)
        setTickets(prev)
        return
      }

      // Reconcile local subscriber map: drop the absorbed entry, fold its
      // followers into the survivor's set.
      setSubsByTicket((prevMap) => {
        const next = new Map(prevMap)
        next.delete(absorbedId)
        if (toAdd.length > 0) {
          const s = new Set(next.get(survivorId) ?? [])
          toAdd.forEach((u) => s.add(u))
          next.set(survivorId, s)
        }
        return next
      })

      // Notify the subscribers of the resulting card (union of both cards'
      // followers). The dispatcher always skips the actor. We snapshot the
      // recipient list here because the absorbed card's rows just cascaded
      // away on delete.
      const recipientUnion = Array.from(
        new Set<string>([...survSubs, ...absSubs]),
      )
      if (recipientUnion.length > 0) {
        void notifyEvent({
          type: 'card_merged',
          ticket_id: survivorId,
          project_id: survivor.project_id,
          survivor_title: survivor.title ?? '',
          absorbed_title: absorbed.title ?? '',
          subscriber_ids: recipientUnion,
        })
      }

      // The user explicitly merged → treat the survivor as seen so the newer
      // activity folded in doesn't re-flag it as « new ».
      markCardSeen(survivorId)
    },
    [updateTicket, markCardSeen],
  )

  /** Move a card across PROJECTS (not just lists within the same one).
   *  Wired to the « Déplacer » entry in TicketCardMenu and the modal
   *  MoveCardDialog. Orchestrates three things in order:
   *
   *    1. Invite the user-picked « needs-access » members into the
   *       destination project's project_members table. RLS only lets
   *       this through when the viewer owns the destination project —
   *       upserts that fail are caught and skipped silently (the modal
   *       has already disabled the checkboxes in that case, so this is
   *       a defensive belt-and-braces).
   *
   *    2. Patch the ticket: set its project_id + list_id. Optimistic
   *       update + rollback on error.
   *
   *    3. Clean up ticket_subscribers rows whose user lost access to
   *       the card (= subscribers who weren't invited and don't belong
   *       to the destination project). Without this they'd remain as
   *       dead rows the RLS would hide from them anyway, but cleaning
   *       up keeps the data tidy.
   *
   *  Failure in any step surfaces via useNotify with the relevant message. */
  const moveCardToProject = useCallback(
    async (params: {
      ticket: Ticket
      destProjectId: string
      destListId: string
      inviteUserIds: string[]
      /** Inbox triage: pin this WhatsApp contact to the destination
       *  project so future messages from them land directly there, and
       *  sweep up any OTHER inbox cards from the same contact. */
      routeSender?: boolean
      senderWaId?: string | null
      senderName?: string | null
    }) => {
      const {
        ticket,
        destProjectId,
        destListId,
        inviteUserIds,
        routeSender,
        senderWaId,
        senderName,
      } = params
      if (
        ticket.project_id === destProjectId &&
        ticket.list_id === destListId &&
        !routeSender
      ) {
        return // no-op
      }

      // 1. Invitations (best-effort — RLS gates these).
      if (inviteUserIds.length > 0) {
        const rows = inviteUserIds.map((uid) => ({
          project_id: destProjectId,
          user_id: uid,
          role: 'member',
          invited_by: user.id,
        }))
        const { error: inviteErr } = await supabase
          .from('project_members')
          .upsert(rows, { onConflict: 'project_id,user_id' })
        if (inviteErr) {
          console.error('Invitations partielles', inviteErr)
          notify({
            title: 'Invitations refusées',
            message:
              "Tu n'as pas pu inviter ces personnes sur le projet de destination. Elles seront retirées de la carte.",
            tone: 'warning',
          })
        }
      }

      // 2. Patch the card (optimistic).
      const prev = ticketsRef.current.find((t) => t.id === ticket.id)
      if (!prev) return
      const optimistic: Ticket = {
        ...prev,
        project_id: destProjectId,
        list_id: destListId,
        updated_at: new Date().toISOString(),
      }
      setTickets((list) =>
        list.map((t) => (t.id === ticket.id ? optimistic : t)),
      )
      const { error: updErr } = await supabase
        .from('tickets')
        .update({ project_id: destProjectId, list_id: destListId })
        .eq('id', ticket.id)
      if (updErr) {
        setTickets((list) => list.map((t) => (t.id === ticket.id ? prev : t)))
        notify({
          title: 'Déplacement échoué',
          message: updErr.message,
          tone: 'error',
        })
        return
      }
      // Notify the (remaining) subscribers of the status change. Lists
      // belong to different projects but we resolve their names from
      // public.lists by id, so this works cross-project.
      if (ticket.list_id !== destListId) {
        void notifyEvent({
          type: 'card_status',
          ticket_id: ticket.id,
          from_list_id: ticket.list_id,
          to_list_id: destListId,
        })
      }

      // 3. Clean up dead subscribers (those without access to the dest
      // project AND not invited). Compute the set of user_ids that DO
      // have access in the dest project, then delete subscribers whose
      // user_id isn't in that set.
      const destProject = projectsRef.current.find(
        (p) => p.id === destProjectId,
      )
      const accessIds = new Set<string>()
      if (destProject?.user_id) accessIds.add(destProject.user_id)
      for (const m of projectMembersRef.current) {
        if (m.project_id === destProjectId) accessIds.add(m.user_id)
      }
      for (const uid of inviteUserIds) accessIds.add(uid)
      const currentSubs = subsByTicketRef.current.get(ticket.id) ?? new Set()
      const stale = [...currentSubs].filter((uid) => !accessIds.has(uid))
      if (stale.length > 0) {
        const { error: subDelErr } = await supabase
          .from('ticket_subscribers')
          .delete()
          .eq('ticket_id', ticket.id)
          .in('user_id', stale)
        if (subDelErr) {
          console.error('Cleanup subscribers failed', subDelErr)
          // Not fatal — realtime will catch up. No user-facing toast.
        }
      }

      // If the user was viewing the source project, the card just
      // disappeared from view but state is consistent. Realtime echoes
      // (project_members + tickets + subs) will reconcile across other
      // viewers' sessions.

      // ── 4. Inbox sender routing (optional) ─────────────────────────
      // If this was an inbox card AND the user asked to also pin the
      // contact + sweep their other inbox cards: write a sender_routing
      // row (so ingest-message sends future WhatsApp messages from this
      // wa_id straight to dest), then move the other open inbox cards
      // from the same contact into the same destination list.
      if (routeSender && senderWaId) {
        const sourceProject = projectsRef.current.find(
          (p) => p.id === ticket.project_id,
        )
        // Only meaningful when the source actually is the owner's
        // inbox. Belt-and-braces — the dialog already gates on this.
        if (sourceProject?.is_inbox && sourceProject.user_id) {
          const { error: routeErr } = await supabase
            .from('sender_routing')
            .upsert(
              {
                owner_user_id: sourceProject.user_id,
                sender_id: senderWaId,
                project_id: destProjectId,
              },
              { onConflict: 'owner_user_id,sender_id' },
            )
          if (routeErr) {
            console.error('sender_routing upsert failed', routeErr)
            notify({
              title: 'Routage du contact échoué',
              message:
                "La carte est déplacée mais les prochains messages de ce contact pourraient encore atterrir dans la boîte de réception. Réessayez depuis la carte.",
              tone: 'warning',
            })
          }

          // Sweep up other open inbox cards from the same sender. We
          // find messages from this contact in the inbox project,
          // then locate the tickets that include them, then re-parent
          // those tickets to the destination list.
          const { data: senderMsgs } = await supabase
            .from('messages')
            .select('id')
            .eq('project_id', sourceProject.id)
            .eq('from_wa_id', senderWaId)
            .limit(500)
          const senderMsgIds = (senderMsgs ?? []).map((r) => r.id as string)
          if (senderMsgIds.length > 0) {
            const { data: otherTickets } = await supabase
              .from('tickets')
              .select('id, source_message_ids')
              .eq('project_id', sourceProject.id)
              .eq('archived', false)
              .is('done_at', null)
              .overlaps('source_message_ids', senderMsgIds)
            const otherIds = (otherTickets ?? [])
              .map((t) => t.id as string)
              .filter((id) => id !== ticket.id)
            if (otherIds.length > 0) {
              const { error: sweepErr } = await supabase
                .from('tickets')
                .update({ project_id: destProjectId, list_id: destListId })
                .in('id', otherIds)
              if (sweepErr) {
                console.error('inbox sweep failed', sweepErr)
              } else {
                notify({
                  title: 'Contact affecté',
                  message: `${
                    senderName?.trim() || senderWaId
                  } est désormais routé vers ce canal — ${
                    otherIds.length
                  } autre${otherIds.length > 1 ? 's' : ''} carte${
                    otherIds.length > 1 ? 's' : ''
                  } déplacée${otherIds.length > 1 ? 's' : ''} aussi.`,
                  tone: 'success',
                })
                return
              }
            }
          }
          if (!routeErr) {
            notify({
              title: 'Contact affecté',
              message: `${
                senderName?.trim() || senderWaId
              } est désormais routé vers ce canal. Les prochains messages arriveront directement ici.`,
              tone: 'success',
            })
          }
        }
      }
    },
    [user.id, notify],
  )

  /** Transcribe a single audio attached to a card and rewrite the card's
   *  title (from Claude's 1–2 sentence summary) + description (from the
   *  full verbatim transcription). Wired to InlineAudio's kebab
   *  « Transcrire » entry, so the user gets an AI rewrite of the card
   *  without opening the detail modal.
   *
   *  Flow:
   *    1. Mark the card as « transcribing » so a small chip appears on
   *       it (the edge function takes a few seconds — without feedback
   *       the user can't tell whether anything is happening).
   *    2. Look up the source message that owns this audio URL.
   *    3. Call the `transcribe-audio` edge function (Whisper +
   *       Claude summary, same one TicketDetailModal already uses).
   *    4. Replace title (with the summary) AND description (with the
   *       transcription) in one go. Falls back to keeping the previous
   *       title/description if either piece is missing.
   *    5. Mark the URL as already-transcribed so the kebab shows
   *       « Déjà transcrit » next time.
   *
   *  Errors surface as toast-style notifications via useNotify. The
   *  transcribing flag is cleared in every exit branch (finally). */
  const transcribeCardAudio = useCallback(
    async (ticket: Ticket, audioUrl: string) => {
      const ids = ticket.source_message_ids
      if (!ids || ids.length === 0) {
        notify({
          title: 'Transcription impossible',
          message: 'Aucun message source associé à cette carte.',
          tone: 'error',
        })
        return
      }

      // Flip the "transcribing" flag for this ticket — the TicketCard
      // reads it and renders a small chip + spinner.
      setTranscribingTicketIds((prev) => {
        const next = new Set(prev)
        next.add(ticket.id)
        return next
      })

      try {
        // Pull only the rows we need + only the columns required to match
        // the URL → message_id. Keeps egress minimal.
        const { data: msgs, error: fetchErr } = await supabase
          .from('messages')
          .select('id, media_urls')
          .in('id', ids)
        if (fetchErr) {
          notify({
            title: 'Transcription impossible',
            message: fetchErr.message,
            tone: 'error',
          })
          return
        }
        const owner = (
          msgs as { id: string; media_urls: string[] }[] | null
        )?.find((m) => m.media_urls?.includes(audioUrl))
        if (!owner) {
          notify({
            title: 'Transcription impossible',
            message: "Le message d'origine de cet audio est introuvable.",
            tone: 'error',
          })
          return
        }

        const { data, error } = await supabase.functions.invoke(
          'transcribe-audio',
          {
            body: {
              message_id: owner.id,
              // Locale drives the system-prompt branch in the edge
              // function so Claude answers (title/summary) in the user's
              // active language.
              locale: userPrefsRef.current.locale ?? i18n.language,
            },
          },
        )
        if (error) {
          let detail = error.message
          try {
            const ctx = (error as { context?: Response }).context
            const body =
              ctx && typeof ctx.json === 'function' ? await ctx.json() : null
            if (body?.error) detail = body.error
          } catch {
            /* keep generic */
          }
          notify({
            title: 'Échec de la transcription',
            message: detail,
            tone: 'error',
          })
          return
        }
        const result = data as {
          transcription?: string
          summary?: string
          title?: string
          is_question?: boolean
          mentioned_date?: string | null
          is_urgent?: boolean
          error?: string
        }
        if (result.error) {
          notify({
            title: 'Échec de la transcription',
            message: result.error,
            tone: 'error',
          })
          return
        }
        const transcription = (result.transcription ?? '').trim()
        // Claude returns SIX useful pieces:
        //   - title          : 3–8 word card heading       → card.title
        //   - summary        : 1–2 sentence digest         → card.description
        //   - is_question    : speaker asks something       → card.ai_is_question
        //   - mentioned_date : ISO date (relative resolved) → card.ai_mentioned_date
        //   - is_urgent      : urgency cues detected        → card.ai_is_urgent (+ auto-priority)
        //   - transcription  : verbatim Whisper output      → kept server-side
        //                      on messages.transcription, accessible from the
        //                      detail modal's Médias section.
        // The verbatim text is NOT what the user wants in the card body
        // (too verbose, too literal). The 1–2 sentence summary is the
        // right level of abstraction for the description; the title is
        // a short heading derived from that summary; the question +
        // date + urgency flags drive the small icons next to the
        // favourite star (and, for urgency, optionally bump priority).
        //
        // Each AI signal is gated by a user preference (default ON):
        //   - ai_detect_question
        //   - ai_detect_date
        //   - ai_detect_urgency  → also gates the auto-priority bump
        //   - ai_auto_priority   → controls JUST the priority bump
        //     (the user can keep the flame icon without the priority).
        const aiTitle = (result.title ?? '').trim()
        const summary = (result.summary ?? '').trim()
        const aiIsQuestion = result.is_question === true
        const aiIsUrgent = result.is_urgent === true
        const aiMentionedDate =
          typeof result.mentioned_date === 'string' && result.mentioned_date.trim()
            ? result.mentioned_date.trim()
            : null
        const prefs = userPrefsRef.current
        const detectQuestion = prefs.ai_detect_question !== false
        const detectDate = prefs.ai_detect_date !== false
        const detectUrgency = prefs.ai_detect_urgency !== false
        const autoPriority = prefs.ai_auto_priority !== false
        if (!transcription) {
          notify({
            title: 'Transcription vide',
            message: "Aucun texte exploitable n'a été extrait de cet audio.",
            tone: 'warning',
          })
          return
        }

        const current = ticketsRef.current.find((t) => t.id === ticket.id)
        // Mark this audio as transcribed-into-description so the kebab's
        // « Transcrire » entry shows up as « Déjà transcrit » (greyed +
        // disabled) and the user doesn't run it twice.
        const already = current?.transcribed_audio_urls ?? []
        const marked = already.includes(audioUrl)
          ? already
          : [...already, audioUrl]

        // Replace title + description. The short AI title and the
        // summary may be empty if Claude failed or returned non-JSON
        // (Whisper succeeded but Claude rate-limited / errored).
        //   - No summary → fall back to the verbatim transcription so
        //     the user still gets something useful in the description.
        //   - No title   → keep the existing title (don't blow it away
        //     with a generated fallback).
        const patch: Partial<Ticket> = {
          description: summary || transcription,
          transcribed_audio_urls: marked,
        }
        if (aiTitle) patch.title = aiTitle
        // ai_is_question is STICKY: once any audio on the card asks a
        // question, the icon stays. A subsequent non-question
        // transcription on another audio of the same card MUST NOT
        // clear it. Only flip to true (never to false from here).
        if (detectQuestion && aiIsQuestion && !current?.ai_is_question) {
          patch.ai_is_question = true
        }
        // ai_mentioned_date OVERRIDES on each run — last transcribed
        // audio wins. If this audio mentions no date but a previous one
        // did, we preserve the older value (don't overwrite with null).
        if (detectDate && aiMentionedDate) {
          patch.ai_mentioned_date = aiMentionedDate
          // Bypass human validation: auto-apply the detected date as the
          // card's schedule (all-day) so it shows circled in the planning +
          // as the card's date chip — no "use detected" click needed. Only
          // the Google Agenda export stays a manual action. We never overwrite
          // an existing schedule (the user's own choice wins).
          if (!current?.scheduled_start) {
            const [y, mo, d] = aiMentionedDate.slice(0, 10).split('-').map(Number)
            if (y && mo && d) {
              patch.scheduled_start = new Date(y, mo - 1, d, 12, 0, 0).toISOString()
              patch.scheduled_end = null
              patch.scheduled_all_day = true
            }
          }
        }
        // ai_is_urgent is STICKY too. When also ai_auto_priority is on
        // AND the card has no priority set yet, bump priority to urgent
        // (we never override a manual choice — the user is the boss).
        if (detectUrgency && aiIsUrgent) {
          if (!current?.ai_is_urgent) patch.ai_is_urgent = true
          if (autoPriority && !current?.priority) patch.priority = 'urgent'
        }
        await updateTicket(ticket.id, patch)
      } finally {
        setTranscribingTicketIds((prev) => {
          if (!prev.has(ticket.id)) return prev
          const next = new Set(prev)
          next.delete(ticket.id)
          return next
        })
      }
    },
    [notify, updateTicket],
  )

  // Subscribe / unsubscribe a user to a ticket. Optimistic update; on a
  // successful subscribe (insert), fire the notification edge function so
  // the user receives an email about being added to the card.
  const toggleSubscriber = useCallback(
    async (ticketId: string, userId: string, subscribe: boolean) => {
      // Snapshot the current set for rollback.
      const prevSet = new Set(subsByTicket.get(ticketId) ?? [])
      const nextSet = new Set(prevSet)
      if (subscribe) nextSet.add(userId)
      else nextSet.delete(userId)
      setSubsByTicket((prev) => {
        const next = new Map(prev)
        if (nextSet.size === 0) next.delete(ticketId)
        else next.set(ticketId, nextSet)
        return next
      })

      const op = subscribe
        ? supabase
            .from('ticket_subscribers')
            .insert({ ticket_id: ticketId, user_id: userId })
        : supabase
            .from('ticket_subscribers')
            .delete()
            .eq('ticket_id', ticketId)
            .eq('user_id', userId)
      const { error } = await op
      if (error) {
        // 23505 = duplicate primary key (already subscribed) — harmless. Any
        // other error rolls the optimistic state back.
        if ((error as { code?: string }).code !== '23505') {
          console.error('toggleSubscriber failed, rolling back:', error)
          setSubsByTicket((prev) => {
            const next = new Map(prev)
            if (prevSet.size === 0) next.delete(ticketId)
            else next.set(ticketId, prevSet)
            return next
          })
          return
        }
      }

      // Best-effort notification emails on subscribe (not on unsubscribe,
      // and never to the actor themselves). Two parallel paths:
      //   - notify-card-subscriber → emails the JOINER ("you were added")
      //   - notify-event card_member → emails the OTHER subscribers ("X joined")
      if (subscribe && userId !== user.id) {
        supabase.functions
          .invoke('notify-card-subscriber', {
            body: { ticket_id: ticketId, user_id: userId },
          })
          .catch((e) => console.warn('notify-card-subscriber failed', e))
        void notifyEvent({
          type: 'card_member',
          ticket_id: ticketId,
          joiner_user_id: userId,
        })
      }
    },
    [subsByTicket, user.id],
  )

  /** Subscribe one or more users to ALL cards of a channel. `existing`
   *  bulk-subscribes them to the current cards; `future` records a default
   *  rule so a DB trigger auto-subscribes them to cards created later. */
  const inviteToChannelCards = useCallback(
    async (
      projectId: string,
      userIds: string[],
      opts: { existing: boolean; future: boolean },
    ) => {
      if (userIds.length === 0) return
      // Future cards → persistent rule (trigger applies it on insert).
      if (opts.future) {
        await supabase
          .from('project_default_subscribers')
          .upsert(
            userIds.map((uid) => ({ project_id: projectId, user_id: uid })),
            { onConflict: 'project_id,user_id' },
          )
      }
      // Existing cards → bulk subscribe now.
      if (opts.existing) {
        const ids = ticketsRef.current
          .filter((t) => t.project_id === projectId)
          .map((t) => t.id)
        if (ids.length) {
          const rows = ids.flatMap((tid) =>
            userIds.map((uid) => ({ ticket_id: tid, user_id: uid })),
          )
          const { error } = await supabase
            .from('ticket_subscribers')
            .upsert(rows, { onConflict: 'ticket_id,user_id' })
          if (!error) {
            setSubsByTicket((prev) => {
              const next = new Map(prev)
              for (const tid of ids) {
                const s = new Set(next.get(tid) ?? [])
                for (const uid of userIds) s.add(uid)
                next.set(tid, s)
              }
              return next
            })
          }
        }
      }
    },
    [],
  )

  const archiveTicket = useCallback(async (id: string, archived: boolean) => {
    const prev = ticketsRef.current.find((t) => t.id === id)
    if (!prev) return
    setTickets((list) =>
      list.map((t) => (t.id === id ? { ...t, archived } : t)),
    )
    const { error } = await supabase.from('tickets').update({ archived }).eq('id', id)
    if (error) {
      console.error('Ticket archive failed, rolling back:', error)
      setTickets((list) => list.map((t) => (t.id === id ? prev : t)))
      return
    }
    // Mirror events: archive and restore are both notifiable. Both
    // share the cardArchived preference toggle server-side.
    if (archived && !prev.archived) {
      void notifyEvent({ type: 'card_archived', ticket_id: id })
    } else if (!archived && prev.archived) {
      void notifyEvent({ type: 'card_restored', ticket_id: id })
    }
  }, [])

  // Archive from the board: play a fade/shrink exit on the card, then archive
  // for real once the animation has finished (so it doesn't just blink away).
  const EXIT_MS = 240
  const archiveCard = useCallback(
    (id: string) => {
      setExitingIds((s) => {
        const n = new Set(s)
        n.add(id)
        return n
      })
      setTimeout(() => {
        setExitingIds((s) => {
          const n = new Set(s)
          n.delete(id)
          return n
        })
        archiveTicket(id, true)
      }, EXIT_MS)
    },
    [archiveTicket],
  )

  // Mark one or more ticket IDs as "freshly entering" → triggers the
  // animate-ticket-in CSS class for ~400ms (matches the 320ms keyframe + a bit
  // of buffer so React can settle). Idempotent: re-marking restarts the timer.
  const ENTER_MS = 400
  const markEntering = useCallback((ids: string | string[]) => {
    const arr = Array.isArray(ids) ? ids : [ids]
    if (arr.length === 0) return
    setEnteringIds((prev) => {
      const next = new Set(prev)
      for (const id of arr) next.add(id)
      return next
    })
    setTimeout(() => {
      setEnteringIds((prev) => {
        const next = new Set(prev)
        for (const id of arr) next.delete(id)
        return next
      })
    }, ENTER_MS)
  }, [])

  /** Trigger the one-shot « new message » bounce on a sidebar project. */
  const markProjectBounce = useCallback((projectId: string) => {
    setBouncingProjectIds((prev) => {
      const next = new Set(prev)
      next.add(projectId)
      return next
    })
    setTimeout(() => {
      setBouncingProjectIds((prev) => {
        const next = new Set(prev)
        next.delete(projectId)
        return next
      })
    }, 700)
  }, [])

  // Apply a Claude-proposed ticket split: archive the original card and
  // replace it with N new cards in the same list. Each new card inherits its
  // OWN subset of source messages + optional per-audio clip ranges, so a vocal
  // containing 3 tasks ends up as 3 cards that each play only their slice.
  const applyCardSplit = useCallback(
    async (originalTicket: Ticket, splits: ProposedSplit[]) => {
      if (splits.length < 2) return
      // Newest cards bubble to the top → stack splits above existing positions.
      const inList = ticketsRef.current.filter(
        (t) => t.list_id === originalTicket.list_id && !t.archived,
      )
      const minExisting = inList.length
        ? Math.min(...inList.map((t) => t.position))
        : splits.length + 1
      const baseTop = minExisting - splits.length

      // Fetch the parent's source messages to know which media URLs each split
      // inherits (a split keeps the media of the messages it owns).
      const sourceIds = originalTicket.source_message_ids ?? []
      const { data: msgs } = sourceIds.length
        ? await supabase
            .from('messages')
            .select('id, media_urls, received_at')
            .in('id', sourceIds)
        : { data: [] as { id: string; media_urls: string[] | null; received_at: string }[] }
      const msgById = new Map(
        (msgs ?? []).map((m) => [m.id as string, m as { id: string; media_urls: string[] | null; received_at: string }]),
      )

      const rows = splits.map((s, i) => {
        // Each split inherits the media of the messages it owns. Falls back to
        // the parent's full media list if Claude didn't tag source ids (older
        // proposals).
        const owned = (s.source_message_ids ?? []).map((id) => msgById.get(id)).filter(Boolean)
        const mediaUrls = owned.length
          ? Array.from(new Set(owned.flatMap((m) => m!.media_urls ?? [])))
          : originalTicket.media_urls
        // Source date = most recent timestamp among owned messages.
        const dates = owned
          .map((m) => m!.received_at)
          .filter(Boolean) as string[]
        const sourceDate =
          dates.length > 0 ? dates.sort().reverse()[0] : originalTicket.source_date
        return {
          project_id: originalTicket.project_id,
          list_id: originalTicket.list_id,
          assignee_id: originalTicket.assignee_id,
          title: s.title.slice(0, 200),
          description: s.description || null,
          // First split lands highest (baseTop), last just above existing —
          // preserves Claude's order top-to-bottom in the column.
          position: baseTop + i,
          source_message_ids: s.source_message_ids ?? sourceIds,
          media_urls: mediaUrls,
          source_date: sourceDate,
          // Logical clipping: empty array = no clipping (full audio). We also
          // enrich each entry with `audio_url` (the actual file URL of the
          // referenced source message) so lightweight views like TicketCard
          // and HoverPreview can clip playback without re-fetching messages.
          audio_clips:
            s.audio_clips && s.audio_clips.length > 0
              ? s.audio_clips.map((c) => {
                  const ref = msgById.get(c.message_id)
                  const audioUrl = (ref?.media_urls ?? []).find((u) =>
                    /\.(ogg|oga|opus|mp3|m4a|aac|wav|amr|webm)(\?|$)/i.test(u),
                  )
                  return { ...c, audio_url: audioUrl ?? c.audio_url }
                })
              : null,
        }
      })

      const { data: inserted, error: insErr } = await supabase
        .from('tickets')
        .insert(rows)
        .select('id')
      if (insErr) {
        console.error('applyCardSplit insert failed', insErr)
        throw insErr
      }
      markEntering((inserted ?? []).map((r) => r.id as string))
      // Archive the parent + clear its pending proposal so the banner stops.
      await archiveTicket(originalTicket.id, true)
      await supabase
        .from('tickets')
        .update({ proposed_splits: null })
        .eq('id', originalTicket.id)
    },
    [archiveTicket, markEntering],
  )

  // Manual message split (from the Messagerie tab): create a NEW card in the
  // same channel from a hand-picked subset of the conversation's messages.
  // NON-destructive: the messages stay attached to the original card too, so
  // an element of a conversation can live in two cards at once. The new card
  // lands at the top of the channel's intake list (« Reçu »).
  const splitMessagesToCard = useCallback(
    async (sourceTicket: Ticket, messageIds: string[]) => {
      if (messageIds.length === 0) return
      // Target list = the channel's intake (« Reçu »); fall back to the
      // source card's list if no intake is flagged.
      const { data: intake } = await supabase
        .from('lists')
        .select('id')
        .eq('project_id', sourceTicket.project_id)
        .eq('is_intake', true)
        .maybeSingle()
      const listId = (intake?.id as string | undefined) ?? sourceTicket.list_id

      // Pull the chosen messages for media + date + a title seed.
      const { data: msgs } = await supabase
        .from('messages')
        .select('id, body, transcription, media_urls, received_at')
        .in('id', messageIds)
      const ordered = (msgs ?? []).slice().sort((a, b) =>
        (a.received_at as string) < (b.received_at as string) ? -1 : 1,
      )
      const mediaUrls = Array.from(
        new Set(ordered.flatMap((m) => (m.media_urls as string[] | null) ?? [])),
      )
      const sourceDate =
        ordered.length > 0
          ? (ordered[ordered.length - 1].received_at as string)
          : sourceTicket.source_date
      const firstText =
        ordered
          .map((m) =>
            ((m.body as string | null)?.trim() ||
              (m.transcription as string | null)?.trim() ||
              '') as string,
          )
          .find((s) => s.length > 0) ?? ''
      const title = firstText
        ? firstText.replace(/\s+/g, ' ').slice(0, 80)
        : i18n.t('detail.split.defaultTitle')

      // Top of the intake list (smaller position = higher).
      const inList = ticketsRef.current.filter(
        (t) => t.list_id === listId && !t.archived,
      )
      const minExisting = inList.length
        ? Math.min(...inList.map((t) => t.position))
        : 1
      const position = minExisting - 1

      const { data: inserted, error } = await supabase
        .from('tickets')
        .insert({
          project_id: sourceTicket.project_id,
          list_id: listId,
          assignee_id: sourceTicket.assignee_id,
          title,
          description: null,
          position,
          source_message_ids: messageIds,
          media_urls: mediaUrls,
          source_date: sourceDate,
        })
        .select('id')
        .single()
      if (error) {
        console.error('splitMessagesToCard insert failed', error)
        throw error
      }
      if (inserted) markEntering([inserted.id as string])
    },
    [markEntering],
  )

  /** Trigger Claude analysis on a ticket — populates `tickets.proposed_splits`. */
  const analyzeTicketSplit = useCallback(async (ticketId: string) => {
    const { data, error } = await supabase.functions.invoke('propose-ticket-split', {
      body: { ticket_id: ticketId },
    })
    if (error) {
      let detail = error.message
      try {
        const ctx = (error as { context?: Response }).context
        const body = ctx && typeof ctx.json === 'function' ? await ctx.json() : null
        if (body?.error) detail = body.error
      } catch { /* keep generic */ }
      throw new Error(detail)
    }
    const result = data as { proposed_splits?: ProposedSplit[]; error?: string }
    if (result.error) throw new Error(result.error)
    return result.proposed_splits ?? []
  }, [])

  /** Dismiss a pending split proposal on a ticket (no card created). */
  const dismissCardSplit = useCallback(async (ticketId: string) => {
    await supabase
      .from('tickets')
      .update({ proposed_splits: null })
      .eq('id', ticketId)
  }, [])

  // Manually create a card in a list (classic Kanban "+ Ajouter une carte").
  const createCard = useCallback(
    async (listId: string, title: string, description?: string) => {
      const t = title.trim()
      if (!t || !selectedId) return
      // Position: honour the user's new_card_position pref. Default 'top'
      // matches historical behaviour (smaller position = higher in list).
      // 'bottom' appends at the end (max + 1, Trello convention).
      const inList = ticketsRef.current.filter((tk) => tk.list_id === listId)
      const placeAtBottom = userPrefsRef.current.new_card_position === 'bottom'
      const position = inList.length
        ? placeAtBottom
          ? Math.max(...inList.map((tk) => tk.position)) + 1
          : Math.min(...inList.map((tk) => tk.position)) - 1
        : 1
      const desc = description?.trim() || null
      const { data, error } = await supabase
        .from('tickets')
        .insert({
          project_id: selectedId,
          list_id: listId,
          title: t,
          description: desc,
          position,
          assignee_id: user.id,
          source_message_ids: [],
          media_urls: [],
        })
        .select()
        .single()
      if (error) {
        console.error('Create card failed:', error)
        return
      }
      markEntering(data.id as string)
      // Add immediately; the realtime echo is de-duped by applyChange.
      setTickets((list) =>
        list.some((x) => x.id === data.id) ? list : [...list, data as Ticket],
      )
      // Notify the project OWNER when a NON-owner (member/guest) adds a
      // card. The dispatcher already excludes the actor, so we skip the
      // call entirely when the creator IS the owner (no-op anyway).
      const proj = projectsRef.current.find((p) => p.id === selectedId)
      if (proj && proj.user_id !== user.id) {
        void notifyEvent({ type: 'card_created', ticket_id: data.id as string })
      }
    },
    [selectedId, user.id, markEntering],
  )

  // ---- List (column) CRUD ----
  const createList = useCallback(
    async (projectId: string, name: string, color: string | null) => {
      const trimmed = name.trim()
      if (!trimmed) return
      const siblings = listsRef.current.filter((l) => l.project_id === projectId)
      const position = siblings.length
        ? Math.max(...siblings.map((l) => l.position)) + 1
        : 0
      const { error } = await supabase
        .from('lists')
        .insert({ project_id: projectId, name: trimmed, position, color })
      if (error) console.error('Create list failed:', error)
      // Realtime INSERT delivers the new list into state.
    },
    [],
  )

  const setListColor = useCallback(
    async (id: string, color: string | null) => {
      const prev = listsRef.current.find((l) => l.id === id)
      if (!prev) return
      setLists((arr) => arr.map((l) => (l.id === id ? { ...l, color } : l)))
      const { error } = await supabase.from('lists').update({ color }).eq('id', id)
      if (error) {
        console.error('Set list color failed, rolling back:', error)
        setLists((arr) => arr.map((l) => (l.id === id ? prev : l)))
      }
    },
    [],
  )

  const renameList = useCallback(async (id: string, name: string) => {
    const trimmed = name.trim()
    if (!trimmed) return
    const prev = listsRef.current.find((l) => l.id === id)
    if (!prev || prev.name === trimmed) return
    setLists((arr) => arr.map((l) => (l.id === id ? { ...l, name: trimmed } : l)))
    const { error } = await supabase.from('lists').update({ name: trimmed }).eq('id', id)
    if (error) {
      console.error('Rename list failed, rolling back:', error)
      setLists((arr) => arr.map((l) => (l.id === id ? prev : l)))
    }
  }, [])

  const deleteList = useCallback(async (id: string) => {
    const prevLists = listsRef.current
    const prevTickets = ticketsRef.current
    // Cascade: tickets in this list are removed via FK on delete.
    setLists((arr) => arr.filter((l) => l.id !== id))
    setTickets((arr) => arr.filter((t) => t.list_id !== id))
    const { error } = await supabase.from('lists').delete().eq('id', id)
    if (error) {
      console.error('Delete list failed, rolling back:', error)
      setLists(prevLists)
      setTickets(prevTickets)
    }
  }, [])

  // Persist a new left-to-right ordering of lists (column drag-and-drop).
  const reorderLists = useCallback(async (orderedIds: string[]) => {
    const prev = listsRef.current
    setLists((arr) =>
      arr.map((l) => {
        const idx = orderedIds.indexOf(l.id)
        return idx >= 0 ? { ...l, position: idx } : l
      }),
    )
    const results = await Promise.all(
      orderedIds.map((id, idx) =>
        supabase.from('lists').update({ position: idx }).eq('id', id),
      ),
    )
    if (results.some((r) => r.error)) {
      console.error('Reorder lists failed, rolling back')
      setLists(prev)
    }
  }, [])

  // Reorder projects in the sidebar — same dense-index pattern as
  // reorderLists. The drop handler in ProjectSidebar passes the new full
  // ordering; we re-stamp positions 0..N-1.
  const reorderProjects = useCallback(async (orderedIds: string[]) => {
    const prev = projectsRef.current
    setProjects((arr) =>
      arr.map((p) => {
        const idx = orderedIds.indexOf(p.id)
        return idx >= 0 ? { ...p, position: idx } : p
      }),
    )
    const results = await Promise.all(
      orderedIds.map((id, idx) =>
        supabase.from('projects').update({ position: idx }).eq('id', id),
      ),
    )
    if (results.some((r) => r.error)) {
      console.error('Reorder projects failed, rolling back')
      setProjects(prev)
    }
  }, [])

  const renameProject = useCallback(async (id: string, name: string) => {
    const trimmed = name.trim()
    if (!trimmed) return
    const prev = projectsRef.current.find((p) => p.id === id)
    if (!prev || prev.name === trimmed) return
    setProjects((list) =>
      list.map((p) => (p.id === id ? { ...p, name: trimmed } : p)),
    )
    const { error } = await supabase
      .from('projects')
      .update({ name: trimmed })
      .eq('id', id)
    if (error) {
      console.error('Project rename failed, rolling back:', error)
      setProjects((list) => list.map((p) => (p.id === id ? prev : p)))
    }
  }, [])

  /** Persist the per-channel reply settings (AI tone + who can reply).
   *  Optimistic update with rollback on error. */
  const updateProjectSettings = useCallback(
    async (
      id: string,
      patch: {
        reply_tone: Project['reply_tone']
        reply_permission: Project['reply_permission']
        reply_suggestions_enabled: Project['reply_suggestions_enabled']
        notify_sound: Project['notify_sound']
      },
    ) => {
      const prev = projectsRef.current.find((p) => p.id === id)
      if (!prev) return
      setProjects((list) =>
        list.map((p) => (p.id === id ? { ...p, ...patch } : p)),
      )
      const { error } = await supabase
        .from('projects')
        .update(patch)
        .eq('id', id)
      if (error) {
        setProjects((list) => list.map((p) => (p.id === id ? prev : p)))
        throw error
      }
    },
    [],
  )

  /** Generic optimistic patch for a couple of project fields — used by
   *  the in-card live tone / suggestions controls. */
  const patchProject = useCallback(
    async (id: string, patch: Partial<Project>) => {
      const prev = projectsRef.current.find((p) => p.id === id)
      if (!prev) return
      setProjects((list) =>
        list.map((p) => (p.id === id ? { ...p, ...patch } : p)),
      )
      const { error } = await supabase.from('projects').update(patch).eq('id', id)
      if (error) {
        setProjects((list) => list.map((p) => (p.id === id ? prev : p)))
        throw error
      }
    },
    [],
  )

  // ── Folders (sidebar grouping of channels) ─────────────────────────
  const createFolder = useCallback(async (rawName: string) => {
    const name = rawName.trim()
    if (!name) return
    const maxPos = folders.reduce((m, f) => Math.max(m, f.position), -1)
    const { data, error } = await supabase
      .from('project_folders')
      .insert({ name, position: maxPos + 1 })
      .select()
      .single()
    if (error) {
      notify({ title: i18n.t('sidebar.folder.createError'), tone: 'error' })
      return
    }
    if (data) setFolders((list) => [...list, data as Folder])
  }, [folders, notify])

  const renameFolder = useCallback(async (id: string, rawName: string) => {
    const name = rawName.trim()
    if (!name) return
    const prev = folders.find((f) => f.id === id)
    if (!prev || prev.name === name) return
    setFolders((list) => list.map((f) => (f.id === id ? { ...f, name } : f)))
    const { error } = await supabase
      .from('project_folders')
      .update({ name })
      .eq('id', id)
    if (error) setFolders((list) => list.map((f) => (f.id === id ? prev : f)))
  }, [folders])

  const reorderFolders = useCallback(async (orderedIds: string[]) => {
    const prev = foldersRef.current
    setFolders((arr) =>
      arr.map((f) => {
        const idx = orderedIds.indexOf(f.id)
        return idx >= 0 ? { ...f, position: idx } : f
      }),
    )
    const results = await Promise.all(
      orderedIds.map((id, idx) =>
        supabase.from('project_folders').update({ position: idx }).eq('id', id),
      ),
    )
    if (results.some((r) => r.error)) setFolders(prev)
  }, [])

  const setFolderColor = useCallback(
    async (id: string, color: string | null) => {
      const prev = folders.find((f) => f.id === id)
      if (!prev) return
      setFolders((list) =>
        list.map((f) => (f.id === id ? { ...f, color } : f)),
      )
      const { error } = await supabase
        .from('project_folders')
        .update({ color })
        .eq('id', id)
      if (error) setFolders((list) => list.map((f) => (f.id === id ? prev : f)))
    },
    [folders],
  )

  const deleteFolder = useCallback(async (id: string) => {
    const prevFolders = folders
    // Channels in the folder fall back to ungrouped (DB sets folder_id null
    // via ON DELETE SET NULL — mirror it optimistically).
    setFolders((list) => list.filter((f) => f.id !== id))
    setProjects((list) =>
      list.map((p) => (p.folder_id === id ? { ...p, folder_id: null } : p)),
    )
    const { error } = await supabase.from('project_folders').delete().eq('id', id)
    if (error) {
      setFolders(prevFolders)
      notify({ title: i18n.t('sidebar.folder.deleteError'), tone: 'error' })
    }
  }, [folders, notify])

  /** Move a channel into a folder (or out, with folderId = null). */
  const assignProjectFolder = useCallback(
    async (projectId: string, folderId: string | null) => {
      const prev = projectsRef.current.find((p) => p.id === projectId)
      if (!prev || prev.folder_id === folderId) return
      setProjects((list) =>
        list.map((p) =>
          p.id === projectId ? { ...p, folder_id: folderId } : p,
        ),
      )
      const { error } = await supabase
        .from('projects')
        .update({ folder_id: folderId })
        .eq('id', projectId)
      if (error) {
        setProjects((list) =>
          list.map((p) => (p.id === projectId ? prev : p)),
        )
      }
    },
    [],
  )

  /** Create a project NOT tied to a WhatsApp import — a Trello-style
   *  empty board the user can populate by hand.
   *
   *  Notes :
   *    - We rely on the DB default for `user_id` (now `auth.uid()`) so
   *      we don't risk sending a stale / wrong id from the client and
   *      tripping the INSERT RLS policy.
   *    - We rely on the DB trigger `projects_default_lists` (which fires
   *      AFTER INSERT) to seed the three default lists (Reçu, En cours,
   *      Terminé). The client only needs to fetch them once the INSERT
   *      returns, so the board is ready immediately without waiting for
   *      the realtime echo.
   *    - `sender_id = manual:<uuid>` satisfies the non-null + unique
   *      (user_id, sender_id) constraints without ever colliding with a
   *      real WhatsApp chat id. */
  const createManualProject = useCallback(
    async (rawName: string) => {
      const name = rawName.trim()
      if (!name) return
      // Position the new project at the bottom of MY projects (others'
      // projects, shared with me, keep their relative order).
      const myMax = projectsRef.current
        .filter((p) => p.user_id === user.id)
        .reduce((m, p) => Math.max(m, p.position), -1)
      const senderId = `manual:${crypto.randomUUID()}`

      const insertPayload = {
        name,
        sender_id: senderId,
        position: myMax + 1,
        // Manual projects have nothing to import — flag them as "done"
        // so the sidebar's progress UI doesn't show a spinner forever.
        import_status: 'done' as const,
        import_progress: 100,
        user_id: user.id,
      }

      const { data: proj, error: projErr } = await supabase
        .from('projects')
        .insert(insertPayload)
        .select()
        .single()
      if (projErr || !proj) {
        console.error('createManualProject failed', {
          error: projErr,
          payload: insertPayload,
          user_id: user.id,
        })
        notify({
          title: i18n.t('notifications.createProjectFailedTitle'),
          message: projErr?.message
            ? `${projErr.message} (${projErr.code ?? '—'})`
            : i18n.t('notifications.createProjectFailedUnknown'),
          tone: 'error',
        })
        return
      }

      // Fetch the three default lists the DB trigger just inserted, so
      // the board renders immediately without waiting for the realtime
      // echoes. If the fetch fails the realtime channel will fill it in
      // a second later — not blocking.
      const { data: seededLists, error: listsErr } = await supabase
        .from('lists')
        .select('*')
        .eq('project_id', proj.id)
        .order('position', { ascending: true })
      if (listsErr) {
        console.warn('createManualProject: lists fetch after insert failed', listsErr)
      }

      // Optimistic local insert + auto-switch (realtime echoes will be
      // deduped by applyChange's id check).
      setProjects((prev) => [...prev, proj as Project])
      if (seededLists && seededLists.length > 0) {
        setLists((prev) => [...prev, ...(seededLists as List[])])
      }
      setSelectedId(proj.id)
    },
    [user.id, notify],
  )

  /** Inbox triage → brand-new channel for one contact, in a single step:
   *  create the project (its sender_id = the contact's wa_id so the 1:1
   *  routing matches too), pin a sender_routing row, then move this card +
   *  every other inbox card from the same contact into the new channel.
   *  Self-contained (doesn't lean on projectsRef, which wouldn't yet hold
   *  the just-created project). */
  const createChannelForInboxSender = useCallback(
    async (params: {
      ticket: Ticket
      name: string
      senderWaId: string | null
      senderName: string | null
    }) => {
      const { ticket, name: rawName, senderWaId, senderName } = params
      const name = rawName.trim()
      if (!name) return
      const inboxProjectId = ticket.project_id
      const myMax = projectsRef.current
        .filter((p) => p.user_id === user.id)
        .reduce((m, p) => Math.max(m, p.position), -1)
      // sender_id = the contact's wa_id → ingest-message's 1:1 lookup also
      // resolves here (sender_routing is the belt-and-braces tier-1 match).
      const senderId = senderWaId || `manual:${crypto.randomUUID()}`

      const { data: proj, error: projErr } = await supabase
        .from('projects')
        .insert({
          name,
          sender_id: senderId,
          position: myMax + 1,
          import_status: 'done' as const,
          import_progress: 100,
          user_id: user.id,
        })
        .select()
        .single()
      if (projErr || !proj) {
        console.error('createChannelForInboxSender: insert failed', projErr)
        notify({
          title: i18n.t('notifications.createProjectFailedTitle'),
          message:
            projErr?.message ?? i18n.t('notifications.createProjectFailedUnknown'),
          tone: 'error',
        })
        return
      }

      // The DB trigger seeds Reçu/En cours/Terminé synchronously — fetch them.
      const { data: seededLists } = await supabase
        .from('lists')
        .select('*')
        .eq('project_id', proj.id)
        .order('position', { ascending: true })
      setProjects((prev) => [...prev, proj as Project])
      if (seededLists && seededLists.length > 0) {
        setLists((prev) => [...prev, ...(seededLists as List[])])
      }
      const intake =
        (seededLists as List[] | null)?.find((l) => l.is_intake) ??
        (seededLists as List[] | null)?.[0] ??
        null
      if (!intake) {
        notify({
          title: i18n.t('notifications.createProjectFailedTitle'),
          message: i18n.t('notifications.createProjectFailedUnknown'),
          tone: 'error',
        })
        setSelectedId(proj.id)
        return
      }

      // Route the contact to the new channel (future messages land here).
      if (senderWaId) {
        const { error: routeErr } = await supabase
          .from('sender_routing')
          .upsert(
            {
              owner_user_id: user.id,
              sender_id: senderWaId,
              project_id: proj.id,
            },
            { onConflict: 'owner_user_id,sender_id' },
          )
        if (routeErr) console.error('sender_routing upsert failed', routeErr)
      }

      // Gather this card + the contact's other open inbox cards to move.
      let toMove = [ticket.id]
      if (senderWaId) {
        const { data: senderMsgs } = await supabase
          .from('messages')
          .select('id')
          .eq('project_id', inboxProjectId)
          .eq('from_wa_id', senderWaId)
          .limit(500)
        const ids = (senderMsgs ?? []).map((r) => r.id as string)
        if (ids.length > 0) {
          const { data: others } = await supabase
            .from('tickets')
            .select('id')
            .eq('project_id', inboxProjectId)
            .eq('archived', false)
            .is('done_at', null)
            .overlaps('source_message_ids', ids)
          toMove = Array.from(
            new Set([ticket.id, ...(others ?? []).map((t) => t.id as string)]),
          )
        }
      }

      // Optimistic move, then persist.
      setTickets((list) =>
        list.map((t) =>
          toMove.includes(t.id)
            ? { ...t, project_id: proj.id, list_id: intake.id }
            : t,
        ),
      )
      const { error: mvErr } = await supabase
        .from('tickets')
        .update({ project_id: proj.id, list_id: intake.id })
        .in('id', toMove)
      if (mvErr) {
        console.error('createChannelForInboxSender: move failed', mvErr)
      }
      setSelectedId(proj.id)
      notify({
        title: 'Canal créé',
        message: `« ${name} » est créé et ${
          senderName?.trim() || senderWaId || 'ce contact'
        } y est routé${
          toMove.length > 1 ? ` — ${toMove.length} cartes déplacées.` : '.'
        }`,
        tone: 'success',
      })
    },
    [user.id, notify],
  )

  // Pick a project's accent color (drives the top-bar dot AND the sidebar
  // tile). Pass `null` to clear back to the hash-based fallback. Optimistic.
  const setProjectColor = useCallback(
    async (id: string, color: string | null) => {
      const prev = projectsRef.current.find((p) => p.id === id)
      if (!prev || prev.color === color) return
      setProjects((list) =>
        list.map((p) => (p.id === id ? { ...p, color } : p)),
      )
      const { error } = await supabase
        .from('projects')
        .update({ color })
        .eq('id', id)
      if (error) {
        console.error('Project color set failed, rolling back:', error)
        setProjects((list) => list.map((p) => (p.id === id ? prev : p)))
      }
    },
    [],
  )

  const archiveProject = useCallback(
    async (id: string, archived: boolean) => {
      const prev = projectsRef.current.find((p) => p.id === id)
      if (!prev) return
      setProjects((list) =>
        list.map((p) => (p.id === id ? { ...p, archived } : p)),
      )
      // If we just archived the selected project, jump to another visible one.
      if (archived && id === selectedId) {
        const next = projectsRef.current.find((p) => p.id !== id && !p.archived)
        setSelectedId(next?.id ?? null)
      }
      const { error } = await supabase
        .from('projects')
        .update({ archived })
        .eq('id', id)
      if (error) {
        console.error('Project archive failed, rolling back:', error)
        setProjects((list) => list.map((p) => (p.id === id ? prev : p)))
      }
    },
    [selectedId],
  )

  const deleteProject = useCallback(
    async (id: string) => {
      const prevProjects = projectsRef.current
      const prevLists = listsRef.current
      const prevTickets = ticketsRef.current
      setProjects((list) => list.filter((p) => p.id !== id))
      setLists((arr) => arr.filter((l) => l.project_id !== id))
      setTickets((list) => list.filter((t) => t.project_id !== id))
      if (id === selectedId) {
        const next = prevProjects.find((p) => p.id !== id && !p.archived)
        setSelectedId(next?.id ?? null)
      }
      // Cascade: lists + tickets + messages are removed via FK on delete.
      const { error } = await supabase.from('projects').delete().eq('id', id)
      if (error) {
        console.error('Project delete failed, rolling back:', error)
        setProjects(prevProjects)
        setLists(prevLists)
        setTickets(prevTickets)
      }
    },
    [selectedId],
  )

  // Hover preview: small delay before showing, instant hide. Avoids flicker
  // when the cursor just sweeps across cards.
  const handleHoverChange = useCallback(
    (ticket: Ticket | null, rect: DOMRect | null) => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current)
      if (ticket && rect) {
        hoverTimer.current = setTimeout(() => {
          setHover({ ticket, rect })
          // Showing the hover preview counts as "seen" → clears the new flag,
          // unless the user set dismissal to "open only".
          if (userPrefsRef.current.new_card_dismiss !== 'open_only') {
            markCardSeen(ticket.id)
          }
        }, 350)
      } else {
        setHover(null)
      }
    },
    [markCardSeen],
  )

  // List of user_ids who can be subscribed to cards in the selected project —
  // = the owner (not stored in project_members) + every member row. Always
  // ordered deterministically so the picker UI is stable.
  const subscribableUserIds = useMemo(() => {
    if (!selected) return [] as string[]
    const ids = new Set<string>([selected.user_id])
    for (const m of projectMembers) {
      if (m.project_id === selected.id) ids.add(m.user_id)
    }
    return Array.from(ids)
  }, [selected, projectMembers])

  // Deduplicated labels used anywhere in the currently-selected project's
  // tickets — surfaced as suggestion chips in the card menu AND in the
  // detail modal's labels popover so the user can reapply without retyping.
  const projectLabels = useMemo(() => {
    if (!selectedId) return [] as string[]
    const seen = new Set<string>()
    for (const tk of tickets) {
      if (tk.project_id !== selectedId) continue
      for (const l of tk.labels ?? []) {
        const v = l.trim()
        if (v) seen.add(v)
      }
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b))
  }, [tickets, selectedId])

  // How many ACTIVE (non-archived) cards still carry each label. A label
  // with 0 active cards is "orphaned" — it lingers in the suggestion list
  // (it's only on archived cards, or all its cards were deleted) and the
  // label-filter dropdown offers to delete it.
  const projectLabelCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    if (!selectedId) return counts
    for (const tk of tickets) {
      if (tk.project_id !== selectedId || tk.archived) continue
      for (const l of tk.labels ?? []) {
        const v = l.trim()
        if (v) counts[v] = (counts[v] ?? 0) + 1
      }
    }
    return counts
  }, [tickets, selectedId])

  // Permanently delete a label from the project: strip it off every card
  // that still references it (archived ones included, so it doesn't come
  // back), behind a confirmation modal. Used from the label-filter
  // dropdown for orphaned labels.
  const deleteProjectLabel = useCallback(
    async (label: string) => {
      const target = label.trim()
      if (!target || !selectedId) return
      const ok = await confirm({
        title: i18n.t('topbar.filterLabelsDeleteTitle', { label: target }),
        message: i18n.t('topbar.filterLabelsDeleteBody', { label: target }),
        confirmLabel: i18n.t('common.delete'),
        tone: 'danger',
      })
      if (!ok) return

      const affected = ticketsRef.current.filter(
        (tk) =>
          tk.project_id === selectedId &&
          (tk.labels ?? []).some((l) => l.trim() === target),
      )
      if (affected.length === 0) {
        // Nothing references it anymore — just drop it from the filter.
        setLabelFilters((prev) => {
          const next = new Set(prev)
          next.delete(target)
          return next
        })
        return
      }
      // Optimistic: strip the label from every affected card.
      const prev = ticketsRef.current
      setTickets((arr) =>
        arr.map((tk) =>
          affected.some((a) => a.id === tk.id)
            ? { ...tk, labels: (tk.labels ?? []).filter((l) => l.trim() !== target) }
            : tk,
        ),
      )
      setLabelFilters((prevF) => {
        const next = new Set(prevF)
        next.delete(target)
        return next
      })
      const results = await Promise.all(
        affected.map((tk) =>
          supabase
            .from('tickets')
            .update({
              labels: (tk.labels ?? []).filter((l) => l.trim() !== target),
            })
            .eq('id', tk.id),
        ),
      )
      const failed = results.some((r) => r.error)
      if (failed) {
        console.error('Label delete partially failed, rolling back')
        setTickets(prev)
        notify({
          title: i18n.t('topbar.filterLabelsDeleteFailed'),
          message: i18n.t('topbar.filterLabelsDeleteFailedBody'),
          tone: 'error',
        })
      }
    },
    [selectedId, confirm, notify],
  )

  // Is this an account owner (vs an invited-only collaborator)? Owners either
  // have no projects yet (brand-new) or own at least one; collaborators only
  // ever joined other people's projects. Drives the empty-state copy.
  const isOwnerAccount = useMemo(
    () =>
      projects.length === 0 || projects.some((p) => p.user_id === user.id),
    [projects, user.id],
  )

  // Open a ticket from anywhere (e.g. search): switch project + open the modal.
  const openTicketGlobal = useCallback(
    (ticketId: string, projectId: string) => {
      setHover(null)
      setSelectedId(projectId)
      setOpenTicketId(ticketId)
      markCardSeen(ticketId)
    },
    [markCardSeen],
  )

  /** Drop a card onto a (personal) channel from the sidebar: move it to
   *  that channel's intake list, select the channel and open the card so
   *  the user sees it freshly landed in « Reçu ». */
  const moveCardToChannel = useCallback(
    (ticketId: string, projectId: string) => {
      const projLists = listsRef.current.filter((l) => l.project_id === projectId)
      if (projLists.length === 0) return
      const intake =
        projLists.find((l) => l.is_intake) ??
        [...projLists].sort((a, b) => a.position - b.position)[0]
      if (!intake) return
      // Land at the top of the intake list.
      const minPos = ticketsRef.current
        .filter((t) => t.list_id === intake.id && !t.archived)
        .reduce((m, t) => Math.min(m, t.position), 0)
      void updateTicket(ticketId, {
        project_id: projectId,
        list_id: intake.id,
        position: minPos - 1,
        done_at: null,
      })
      // Switch to the destination channel — but DON'T open the card detail;
      // the user just wants to see it landed at the top of « Reçu ».
      setSelectedId(projectId)
    },
    [updateTicket],
  )

  // Finish first-time onboarding: persist the confirmed company + mandatory
  // name, set onboarded=true, then the gate clears and the board renders.
  const completeOnboarding = useCallback(
    async (patch: {
      first_name: string
      last_name: string
      company_legal_name: string | null
    }) => {
      const display = `${patch.first_name} ${patch.last_name}`.trim()
      setProfiles((list) =>
        list.map((p) =>
          p.id === user.id
            ? {
                ...p,
                first_name: patch.first_name,
                last_name: patch.last_name,
                company_legal_name: patch.company_legal_name,
                display_name: display || p.display_name,
                onboarded: true,
              }
            : p,
        ),
      )
      await supabase
        .from('profiles')
        .update({
          first_name: patch.first_name,
          last_name: patch.last_name,
          company_legal_name: patch.company_legal_name,
          display_name: display || undefined,
          onboarded: true,
        })
        .eq('id', user.id)
      void supabase.auth.updateUser({ data: { display_name: display } })
      // First board access → let the admin(s) who invited this person know
      // they've connected and started participating. Dedicated edge function
      // resolves the recipients (inviters / owners) from the actor's
      // project memberships and emails them. Best-effort, never blocks.
      void supabase.functions
        .invoke('notify-member-seen', { body: {} })
        .catch((e) => console.warn('notify-member-seen failed', e))
    },
    [user.id],
  )

  const openTicket = tickets.find((t) => t.id === openTicketId) ?? null

  // First-time onboarding gate: new invitees must confirm their company and
  // set a mandatory first/last name before reaching the board.
  const myProfile = profilesById[user.id]
  if (!loading && myProfile && myProfile.onboarded === false) {
    return (
      <Onboarding
        profile={myProfile}
        onComplete={completeOnboarding}
        onSignOut={onSignOut}
      />
    )
  }

  return (
    <div className="flex h-screen w-screen overflow-hidden">
      <ProjectSidebar
        projects={visibleProjects}
        selectedId={selectedId}
        currentUserId={user.id}
        onSelect={setSelectedId}
        ticketCounts={ticketCounts}
        newCountByProject={newCountByProject}
        onRename={renameProject}
        onArchive={archiveProject}
        onDelete={deleteProject}
        onOpenSettings={(id) => setSettingsProjectId(id)}
        onInviteToCards={(id) => setInviteCardsProjectId(id)}
        bouncingProjectIds={bouncingProjectIds}
        projectFlames={flameByProject}
        onReorder={reorderProjects}
        onCreateProject={createManualProject}
        folders={sortedFolders}
        onCreateFolder={createFolder}
        onRenameFolder={renameFolder}
        onSetFolderColor={setFolderColor}
        onReorderFolders={reorderFolders}
        onDeleteFolder={deleteFolder}
        onAssignFolder={assignProjectFolder}
        cardDragActive={cardDragActive}
        showArchived={showArchived}
        archivedCount={archivedCount}
        onToggleArchived={() => setShowArchived((v) => !v)}
      />
      <main className="flex min-w-0 flex-1 flex-col bg-[var(--color-bg)]">
        <TopBar
          user={user}
          project={selected}
          archivedCount={archivedTickets.length}
          matchedArchivedCount={matchedArchivedCount}
          memberCount={subscribableUserIds.length}
          memberIds={subscribableUserIds}
          profilesById={profilesById}
          onOpenArchives={() => setArchivesOpen(true)}
          onOpenMembers={() => setMembersOpen(true)}
          onOpenProfile={() => setProfileOpen(true)}
          onSignOut={onSignOut}
          onOpenTicket={openTicketGlobal}
          onSearchChange={setSearchQuery}
          onRenameProject={renameProject}
          onSetProjectColor={setProjectColor}
          favoritesOnly={favoritesOnly}
          favoriteCount={favoriteCount}
          onToggleFavoritesOnly={() => setFavoritesOnly((v) => !v)}
          awaitingOnly={awaitingOnly}
          awaitingCount={awaitingCount}
          onToggleAwaitingOnly={() => setAwaitingOnly((v) => !v)}
          aiFilters={aiFilters}
          aiFilterCounts={aiFilterCounts}
          onToggleAiFilter={toggleAiFilter}
          availableLabels={projectLabels}
          labelCardCounts={projectLabelCounts}
          onDeleteLabel={deleteProjectLabel}
          selectedLabels={labelFilters}
          onToggleLabel={toggleLabelFilter}
          onClearLabels={clearLabelFilters}
          timeFilter={timeFilter}
          onTimeFilterChange={setTimeFilter}
          boardView={boardView}
          onBoardViewChange={(v) => updatePreferences({ board_view: v })}
          toolbarLabels={userPrefs.toolbar_labels !== false}
          compactCards={
            visibleLists.length > 0 &&
            visibleLists.every((l) => compactListIds.has(l.id))
          }
          onToggleCompactCards={() => {
            // If every visible list is compact, turn them all off; otherwise
            // mark every visible list as compact. Lets the global button feel
            // like a master switch on top of the per-list state.
            const allOn =
              visibleLists.length > 0 &&
              visibleLists.every((l) => compactListIds.has(l.id))
            const cur = new Set(userPrefs.compact_lists ?? [])
            if (allOn) {
              for (const l of visibleLists) cur.delete(l.id)
            } else {
              for (const l of visibleLists) cur.add(l.id)
            }
            updatePreferences({ compact_lists: Array.from(cur) })
          }}
        />
        <div className="min-h-0 flex-1">
          {loading ? (
            <div className="flex h-full items-center justify-center text-sm text-[var(--color-text-dim)]">
              Chargement…
            </div>
          ) : !selected ? (
            <EmptyState isOwnerAccount={isOwnerAccount} />
          ) : boardView === 'list' ? (
            <BoardListView
              lists={visibleLists}
              tickets={matchedTickets}
              profilesById={profilesById}
              subsByTicket={subsByTicket}
              newTicketIds={newTicketIds}
              activeListIds={shownListIdSet}
              onToggleList={toggleShownList}
              onShowAllLists={showAllLists}
              intakeListId={
                visibleLists.find((l) => l.is_intake)?.id ?? null
              }
              onCreateCard={createCard}
              onViewMedia={(urls) => {
                setHover(null)
                setLightboxUrls(urls)
              }}
              onTicketClick={(t) => {
                setHover(null)
                setOpenTicketId(t.id)
                markCardSeen(t.id)
              }}
            />
          ) : (
            <KanbanBoard
              lists={visibleLists}
              tickets={matchedTickets}
              projectId={selected.id}
              isInbox={selected.is_inbox === true}
              searchActive={searchActive}
              profilesById={profilesById}
              compactListIds={compactListIds}
              newCardPosition={userPrefs.new_card_position ?? 'top'}
              onToggleListCompact={toggleListCompact}
              subscribableUserIds={subscribableUserIds}
              subsByTicket={subsByTicket}
              currentUserId={user.id}
              onToggleSubscriber={toggleSubscriber}
              exitingIds={exitingIds}
              enteringIds={enteringIds}
              newTicketIds={newTicketIds}
              onUpdateTicket={updateTicket}
              onCreateCard={createCard}
              onArchiveCard={archiveCard}
              onDeleteCard={deleteCardWithUndo}
              onMarkSeen={markCardSeen}
              onCreateList={createList}
              onRenameList={renameList}
              onDeleteList={deleteList}
              onSetListColor={setListColor}
              onReorderLists={reorderLists}
              onTicketClick={(t) => {
                setHover(null)
                setOpenTicketId(t.id)
                markCardSeen(t.id)
              }}
              onMoveCard={(t) => {
                setHover(null)
                setMoveTicketId(t.id)
              }}
              onReplyCard={(t) => {
                setHover(null)
                setReplyTicketId(t.id)
              }}
              onViewMedia={(urls) => {
                setHover(null)
                setLightboxUrls(urls)
              }}
              onViewAudio={(urls, t) => {
                setHover(null)
                setAudioPanel({ urls, ticket: t })
              }}
              onTranscribeAudio={transcribeCardAudio}
              transcribingTicketIds={transcribingTicketIds}
              audioSpeed={audioSpeed}
              onAudioSpeedChange={setAudioSpeed}
              onHoverChange={handleHoverChange}
              onCardDragChange={setCardDragActive}
              onMoveCardToChannel={moveCardToChannel}
              onRequestMerge={requestMerge}
            />
          )}
        </div>
      </main>
      {mergeReq &&
        (() => {
          const survivor = tickets.find((t) => t.id === mergeReq.survivorId)
          const absorbed = tickets.find((t) => t.id === mergeReq.absorbedId)
          if (!survivor || !absorbed) return null
          return (
            <MergeCardsModal
              survivor={survivor}
              absorbed={absorbed}
              busy={mergeBusy}
              onCancel={() => {
                if (mergeBusy) return
                setMergeReq(null)
              }}
              onConfirm={async () => {
                setMergeBusy(true)
                try {
                  await mergeTickets(mergeReq.survivorId, mergeReq.absorbedId)
                } finally {
                  setMergeBusy(false)
                  setMergeReq(null)
                }
              }}
            />
          )
        })()}
      <TicketDetailModal
        ticket={openTicket}
        project={
          openTicket
            ? projects.find((p) => p.id === openTicket.project_id) ?? null
            : null
        }
        lists={visibleLists}
        profilesById={profilesById}
        cardTranscribing={!!openTicket && transcribingTicketIds.has(openTicket.id)}
        onTranscribingChange={setTicketTranscribing}
        autoTranscribe={userPrefs.auto_transcribe === true}
        aiDetect={{
          date: userPrefs.ai_detect_date !== false,
          question: userPrefs.ai_detect_question !== false,
          urgent: userPrefs.ai_detect_urgency !== false,
          autoPriority: userPrefs.ai_auto_priority !== false,
        }}
        subscribableUserIds={subscribableUserIds}
        subscriberIds={openTicket ? subsByTicket.get(openTicket.id) ?? null : null}
        currentUserId={user.id}
        onToggleSubscriber={toggleSubscriber}
        projectLabels={projectLabels}
        onOpenMembers={() => setMembersOpen(true)}
        onClose={() => setOpenTicketId(null)}
        onUpdate={updateTicket}
        onDelete={deleteTicket}
        onArchive={archiveCard}
        onAnalyzeSplit={analyzeTicketSplit}
        onApplySplit={applyCardSplit}
        onDismissSplit={dismissCardSplit}
        onSplitMessagesToCard={splitMessagesToCard}
        audioSpeed={audioSpeed}
        onAudioSpeedChange={setAudioSpeed}
        onMoveCard={(t) => {
          // Close the detail modal first — once the card moves, the
          // currently-open ticket prop becomes stale and the user is
          // probably in a different project anyway.
          setOpenTicketId(null)
          setMoveTicketId(t.id)
        }}
        onPatchProject={patchProject}
      />
      <ReplyDialog
        ticket={tickets.find((t) => t.id === replyTicketId) ?? null}
        project={
          replyTicketId
            ? projects.find(
                (p) =>
                  p.id ===
                  tickets.find((t) => t.id === replyTicketId)?.project_id,
              ) ?? null
            : null
        }
        currentUserId={user.id}
        subscriberIds={
          replyTicketId ? subsByTicket.get(replyTicketId) ?? null : null
        }
        subscribableUserIds={subscribableUserIds}
        profilesById={profilesById}
        onClose={() => setReplyTicketId(null)}
      />
      <MoveCardDialog
        ticket={tickets.find((t) => t.id === moveTicketId) ?? null}
        sourceProject={
          moveTicketId
            ? projects.find(
                (p) =>
                  p.id ===
                  tickets.find((t) => t.id === moveTicketId)?.project_id,
              ) ?? null
            : null
        }
        projects={projects}
        lists={lists}
        projectMembers={projectMembers}
        subscriberIds={
          moveTicketId
            ? subsByTicket.get(moveTicketId) ?? new Set()
            : new Set()
        }
        profilesById={profilesById}
        currentUserId={user.id}
        onClose={() => setMoveTicketId(null)}
        onConfirm={async (p) => {
          const t = tickets.find((x) => x.id === moveTicketId)
          if (!t) return
          await moveCardToProject({
            ticket: t,
            destProjectId: p.destProjectId,
            destListId: p.destListId,
            inviteUserIds: p.inviteUserIds,
            routeSender: p.routeSender ?? false,
            senderWaId: p.senderWaId ?? null,
            senderName: p.senderName ?? null,
          })
        }}
        onCreateChannel={async (p) => {
          const t = tickets.find((x) => x.id === moveTicketId)
          if (!t) return
          await createChannelForInboxSender({
            ticket: t,
            name: p.name,
            senderWaId: p.senderWaId,
            senderName: p.senderName,
          })
        }}
      />
      <HoverPreview
        ticket={hover?.ticket ?? null}
        anchor={hover?.rect ?? null}
        profilesById={profilesById}
      />
      <MediaLightbox urls={lightboxUrls} onClose={() => setLightboxUrls(null)} />
      <AudioPanel
        open={!!audioPanel}
        urls={audioPanel?.urls ?? []}
        ticket={audioPanel?.ticket ?? null}
        autoTranscribe={userPrefs.auto_transcribe === true}
        onClose={() => setAudioPanel(null)}
      />
      {profileOpen && (
        <ProfileModal
          open
          user={user}
          profile={profilesById[user.id] ?? null}
          preferences={userPrefs}
          onUpdatePreferences={updatePreferences}
          onUpdateProfile={updateProfileFields}
          isAccountOwner={projects.some((p) => p.user_id === user.id)}
          onProfilePatched={({ display_name, avatar_url }) => {
            // Optimistic local update so the avatar / name in the picker,
            // sidebar, etc. refreshes immediately — don't wait for realtime.
            setProfiles((arr) =>
              arr.map((p) =>
                p.id === user.id
                  ? { ...p, display_name, avatar_url }
                  : p,
              ),
            )
          }}
          onClose={() => setProfileOpen(false)}
        />
      )}
      <ArchivedCardsModal
        open={archivesOpen}
        projectName={selected?.name ?? ''}
        tickets={archivedTickets}
        lists={visibleLists}
        searchQuery={searchQuery}
        onClose={() => setArchivesOpen(false)}
        onRestore={(id) => archiveTicket(id, false)}
        onDelete={deleteTicket}
        onViewMedia={(urls) => setLightboxUrls(urls)}
      />
      <ProjectMembersModal
        open={membersOpen}
        project={selected}
        currentUserId={user.id}
        profilesById={profilesById}
        onClose={() => setMembersOpen(false)}
      />
      <ProjectSettingsModal
        project={
          settingsProjectId
            ? projects.find((p) => p.id === settingsProjectId) ?? null
            : null
        }
        isOwner={
          !!settingsProjectId &&
          projects.find((p) => p.id === settingsProjectId)?.user_id === user.id
        }
        onClose={() => setSettingsProjectId(null)}
        onSave={updateProjectSettings}
      />
      <InviteToCardsModal
        project={
          inviteCardsProjectId
            ? projects.find((p) => p.id === inviteCardsProjectId) ?? null
            : null
        }
        currentUserId={user.id}
        memberIds={
          inviteCardsProjectId
            ? Array.from(
                new Set<string>([
                  ...(projects.find((p) => p.id === inviteCardsProjectId)
                    ? [
                        projects.find((p) => p.id === inviteCardsProjectId)!
                          .user_id,
                      ]
                    : []),
                  ...projectMembers
                    .filter((m) => m.project_id === inviteCardsProjectId)
                    .map((m) => m.user_id),
                ]),
              )
            : []
        }
        profilesById={profilesById}
        cardCount={
          inviteCardsProjectId
            ? tickets.filter(
                (t) => t.project_id === inviteCardsProjectId && !t.archived,
              ).length
            : 0
        }
        onClose={() => setInviteCardsProjectId(null)}
        onInvite={inviteToChannelCards}
      />
    </div>
  )
}

function EmptyState({ isOwnerAccount }: { isOwnerAccount: boolean }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] shadow-[var(--shadow-soft)]">
        <LayoutGrid className="h-7 w-7" strokeWidth={2.2} />
      </div>
      <div className="text-[17px] font-semibold text-[var(--color-text-strong)]">
        Aucun projet pour l'instant
      </div>
      <div className="max-w-md text-[13px] leading-relaxed text-[var(--color-text)]">
        {isOwnerAccount
          ? "Vos canaux apparaîtront ici. Les messages WhatsApp entrants créent automatiquement des cartes ; vous pouvez aussi transformer une carte de la boîte de réception en canal."
          : "Aucun projet ne vous a été partagé pour le moment. Le propriétaire d'un projet doit vous inviter pour que vous puissiez collaborer."}
      </div>
    </div>
  )
}

interface RealtimePayload<T> {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE'
  new: T
  old: Partial<T>
}

/** Per-user persistence of the active project so reloads / re-opens
 *  land back on the project the user was last looking at, instead of
 *  always snapping to the first project in the sidebar.
 *
 *  Scoped by user id so the same browser used by two accounts doesn't
 *  cross-pollinate (and so logout doesn't leak a previous session's
 *  selection back into the new login).
 *
 *  localStorage swallows errors silently (Safari private mode, quota
 *  exhaustion, disabled storage) — failure is a no-op, the user just
 *  falls back to the first project. */
const LAST_PROJECT_KEY = (userId: string) => `msgban:lastProject:${userId}`

function lastProjectIdFor(userId: string): string | null {
  try {
    return localStorage.getItem(LAST_PROJECT_KEY(userId))
  } catch {
    return null
  }
}

function setLastProjectIdFor(userId: string, projectId: string): void {
  try {
    localStorage.setItem(LAST_PROJECT_KEY(userId), projectId)
  } catch {
    // ignored
  }
}

function applyChange<T extends { id: string }>(
  list: T[],
  payload: RealtimePayload<T>,
): T[] {
  if (payload.eventType === 'INSERT') {
    if (list.some((x) => x.id === payload.new.id)) return list
    return [...list, payload.new]
  }
  if (payload.eventType === 'UPDATE') {
    return list.map((x) => (x.id === payload.new.id ? payload.new : x))
  }
  if (payload.eventType === 'DELETE') {
    const id = (payload.old as { id?: string }).id
    return list.filter((x) => x.id !== id)
  }
  return list
}
